import React, { useState, useEffect, useRef } from 'react';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Tooltip,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
  InputAdornment
} from '@mui/material';
import {
  KeyboardBackspace as KeyboardBackspaceIcon,
  Add as AddIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  VerifiedUser as VerifiedUserIcon,
  PendingActions as PendingActionsIcon,
  Assignment as AssignmentIcon,
  Description as DescriptionIcon,
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon,
  Search as SearchIcon,
  Upload as UploadIcon,
  AttachMoney as AttachMoneyIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  ShowChart as ShowChartIcon,
  AccessTime as AccessTimeIcon,
  TrendingDown as TrendingDownIcon,
  MonetizationOn as MonetizationOnIcon,
  SentimentSatisfied as SentimentSatisfiedIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Download as DownloadIcon,
  Chat as ChatIcon,
  OpenInNew as OpenInNewIcon,
  Assessment as AssessmentIcon,
  Lock as LockIcon,
  Refresh as RefreshIcon,
  Folder as FolderIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { environment } from '../../environments/environment';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from 'recharts';
import { GoogleDriveService, GoogleDriveFile, GoogleDriveAuthState, GoogleDriveServiceCallbacks, GoogleDriveFolder, GoogleDriveBreadcrumb } from '../api/googleDriveService';
import rbacApi from '../api/rbacApi';

// Bot icon (placeholder - you can replace with actual bot image)
const botIcon = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIyMCIgZmlsbD0iIzlDMjdCMCIvPgogIDx0ZXh0IHg9IjIwIiB5PSIyNSIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IndoaXRlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmb250LXdlaWdodD0iYm9sZCI+QUk8L3RleHQ+Cjwvc3ZnPgo=';

// Upload Method Components (exact match from SurveyInsights.tsx)
const UploadMethodCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  border: '2px solid transparent',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(156, 39, 176, 0.15)',
  },
  '&.selected': {
    borderColor: '#9C27B0',
    backgroundColor: 'rgba(156, 39, 176, 0.05)',
  }
}));

const UploadMethodIcon = styled(Box)(({ theme }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(156, 39, 176, 0.1)',
  color: '#9C27B0',
  marginBottom: theme.spacing(1),
}));

// Folder Tree Item Component
const FolderTreeItem = ({
  folder,
  onNavigate,
  onCloseDialog,
  level = 0
}: {
  folder: GoogleDriveFolder;
  onNavigate: (folderId: string) => void;
  onCloseDialog: () => void;
  level?: number;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleToggle = async () => {
    if (!isExpanded) {
      setIsLoading(true);
      try {
        await onNavigate(folder.id);
        setIsExpanded(true);
      } catch (error) {
        console.error('Error navigating to folder:', error);
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsExpanded(false);
    }
  };

  return (
    <Box sx={{ ml: level * 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          cursor: 'pointer',
          borderRadius: 1,
          '&:hover': {
            backgroundColor: '#f3f4f6'
          }
        }}
        onClick={handleToggle}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 16,
          height: 16
        }}>
          {isLoading ? (
            <CircularProgress size={12} sx={{ color: '#6b7280' }} />
          ) : (
            <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
              {isExpanded ? '▼' : '▶'}
            </Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: '0.75rem', color: '#374151' }}>
          📁 {folder.name}
        </Typography>
      </Box>
    </Box>
  );
};

// Styled Components
const FullScreenContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'white',
  zIndex: 9999,
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(3)
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  border: '1px solid #E5E9EF',
  borderRadius: '12px !important',
  marginBottom: '12px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: '0 0 12px 0',
    borderColor: '#9C27B0',
    boxShadow: '0 4px 12px rgba(156, 39, 176, 0.1)',
  }
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: '16px 20px',
  '&.Mui-expanded': {
    minHeight: 'auto',
  },
  '& .MuiAccordionSummary-content': {
    margin: '0',
    '&.Mui-expanded': {
      margin: '0',
    }
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: '#9C27B0',
  }
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: '0 20px 20px 20px',
  borderTop: '1px solid #F0F0F0',
}));

interface MessageBubbleProps {
  sender: 'user' | 'bot';
  children?: React.ReactNode;
  sx?: any;
}

const MessageBubble = ({ sender, children, sx }: MessageBubbleProps) => (
  <Box
    sx={{
      backgroundColor: sender === 'user'
        ? 'rgba(156, 39, 176, 0.1)'
        : '#FFFFFF',
      border: sender === 'user'
        ? '1px solid rgba(156, 39, 176, 0.2)'
        : '1px solid #E5E9EF',
      borderRadius: '16px',
      padding: '12px 16px',
      maxWidth: '85%',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      ...sx
    }}
  >
    {children}
  </Box>
);

// JSON Viewer Component with collapsible sections
interface JsonViewerProps {
  data: any;
  level?: number;
}

const JsonViewer = ({ data, level = 0 }: JsonViewerProps) => {
  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});

  const toggleExpanded = (key: string) => {
    setExpanded(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const renderValue = (value: any, key: string, currentLevel: number) => {
    const padding = (currentLevel + 1) * 2;

    if (value === null) {
      return (
        <Box sx={{ pl: padding, display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
          <Typography variant="body2" sx={{ color: '#374151', fontWeight: 500, fontFamily: 'monospace' }}>
            {key}:
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', fontFamily: 'monospace' }}>
            null
          </Typography>
        </Box>
      );
    }

    if (typeof value === 'string') {
      return (
        <Box sx={{ pl: padding, display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
          <Typography variant="body2" sx={{ color: '#374151', fontWeight: 500, fontFamily: 'monospace' }}>
            {key}:
          </Typography>
          <Typography variant="body2" sx={{ color: '#059669', fontFamily: 'monospace' }}>
            "{value}"
          </Typography>
        </Box>
      );
    }

    if (typeof value === 'number') {
      return (
        <Box sx={{ pl: padding, display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
          <Typography variant="body2" sx={{ color: '#374151', fontWeight: 500, fontFamily: 'monospace' }}>
            {key}:
          </Typography>
          <Typography variant="body2" sx={{ color: '#DC2626', fontFamily: 'monospace' }}>
            {value}
          </Typography>
        </Box>
      );
    }

    if (typeof value === 'boolean') {
      return (
        <Box sx={{ pl: padding, display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
          <Typography variant="body2" sx={{ color: '#374151', fontWeight: 500, fontFamily: 'monospace' }}>
            {key}:
          </Typography>
          <Typography variant="body2" sx={{ color: '#7C3AED', fontFamily: 'monospace' }}>
            {value.toString()}
          </Typography>
        </Box>
      );
    }

    if (Array.isArray(value)) {
      const isExpanded = expanded[key] !== false;
      return (
        <Box sx={{ pl: padding }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              '&:hover': { backgroundColor: '#F3F4F6' },
              borderRadius: '4px'
            }}
            onClick={() => toggleExpanded(key)}
          >
            <Typography
              variant="body2"
              sx={{
                color: isExpanded ? '#9C27B0' : '#374151',
                fontWeight: 600,
                fontFamily: 'monospace'
              }}
            >
              {isExpanded ? '▼' : '▶'} {key}:
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', fontFamily: 'monospace' }}>
              [{value.length}]
            </Typography>
          </Box>
          {isExpanded && (
            <Box>
              {value.map((item: any, index: number) => (
                <Box key={index} sx={{ py: 0.5 }}>
                  <Typography variant="body2" sx={{ color: '#6B7280', fontFamily: 'monospace' }}>
                    [{index}]:
                  </Typography>
                  <JsonViewer data={item} level={currentLevel + 1} />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      );
    }

    if (typeof value === 'object') {
      const isExpanded = expanded[key] !== false;
      const keys = Object.keys(value);
      return (
        <Box sx={{ pl: padding }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              '&:hover': { backgroundColor: '#F3F4F6' },
              borderRadius: '4px'
            }}
            onClick={() => toggleExpanded(key)}
          >
            <Typography
              variant="body2"
              sx={{
                color: isExpanded ? '#9C27B0' : '#374151',
                fontWeight: 600,
                fontFamily: 'monospace'
              }}
            >
              {isExpanded ? '▼' : '▶'} {key}:
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', fontFamily: 'monospace' }}>
              {'{'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', fontFamily: 'monospace' }}>
              {keys.length} keys
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', fontFamily: 'monospace' }}>
              {'}'}
            </Typography>
          </Box>
          {isExpanded && (
            <Box>
              {keys.map((k) => (
                <Box key={k} sx={{ py: 0.5 }}>
                  {renderValue(value[k], k, currentLevel + 1)}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      );
    }

    return null;
  };

  if (typeof data === 'object' && data !== null) {
    const keys = Object.keys(data);
    return (
      <Box>
        {keys.map((key) => (
          <Box key={key}>{renderValue(data[key], key, level)}</Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, textAlign: 'left' }}>
      <Typography sx={{ color: '#6B7280' }}>
        Empty
      </Typography>
    </Box>
  );
};


interface ChatMessage {
  id: number;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

export type Project = {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  applicationsCount: number;
};

export type Application = {
  id: string;
  projectId: string;
  name: string;
  city?: string;
  state?: string;
  status: 'draft' | 'processing' | 'completed' | 'requires_review' | 'unknown';
  createdAt: string;
  documentsCount: number;
  extractedData?: {
    personalInfo: {
      name: string;
      panCard: string;
      aadharCard: string;
      email: string;
      phone: string;
    };
    financialInfo: {
      annualIncome: string;
      employmentType: string;
      creditScore: string;
    };
    loanDetails: {
      requestedAmount: string;
      propertyValue: string;
      downPayment: string;
    };
  };
  documents: Array<{
    id: string;
    name: string;
    type: string;
    category: 'pan_card' | 'aadhar_card' | 'salary_slip' | 'bank_statement' | 'property_document' | 'other';
    url: string;
    extractedData: Record<string, any>;
    uploadedAt: string;
  }>;
  // Enhanced dummy data for better UI
  dummyData?: {
    status: string;
    loanType: string;
    bank: string;
    city: string;
    state: string;
    riskScore: number;
    processingTime: number;
    approvalRate: number;
    lastUpdated: string;
    applicationType: string;
    priority: string;
    assignedTo: string;
    estimatedCompletion: string;
  };
};

export type Screen = 'home' | 'applications' | 'upload' | 'details' | 'add_documents' | 'loan_form';

export type UserRole = 'admin' | 'user';

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
}

const LoanProcessing = () => {
  const navigate = useNavigate();

  // State management - matching DocsInsights.tsx structure
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFormData, setUploadFormData] = useState({
    topic: '',
    keywords: [] as string[]
  });

  // Upload method selection state (exact match from SurveyInsights.tsx)
  const [uploadMethod, setUploadMethod] = useState<'device' | 'url' | 'google-drive'>('device');
  const [fileUrl, setFileUrl] = useState('');
  const [urlValidationError, setUrlValidationError] = useState('');
  const [isUrlValidating, setIsUrlValidating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [newApplicationDialogOpen, setNewApplicationDialogOpen] = useState(false);
  const [newApplicationFormData, setNewApplicationFormData] = useState({
    name: '',
    topic: '',
    keywords: [] as string[]
  });
  const [applications, setApplications] = useState<Application[]>([]);
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);

  // Document selection state for chatbot context
  const [selectedDocument, setSelectedDocument] = useState<any>(null);

  // Accordion expansion state - only one document expanded at a time
  const [expandedDocument, setExpandedDocument] = useState<string | null>(null);

  // Chat state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatQuery, setChatQuery] = useState('');
  const [chatResponse, setChatResponse] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 1,
      text: "Hello! I'm your AI assistant. How can I help you analyze loan applications?",
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [suggestedQuestions, setSuggestedQuestions] = useState([
    { text: "Analyze this loan application", icon: <TrendingUpIcon sx={{ fontSize: 16 }} /> },
    { text: "What's the risk assessment?", icon: <ShowChartIcon sx={{ fontSize: 16 }} /> },
  ]);

  // Project management state
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isProjectDialogOpen, setIsProjectDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState({
    project_name: '',
    project_description: '',
    project_type: 'EKYC_DOCU',
    period_start_date: new Date().toISOString().split('T')[0],
    period_end_date: new Date().toISOString().split('T')[0],
    period_type: 'daily',
    source: 'manual',
    active: true,
    status: 'active'
  });
  const [isSubmittingProject, setIsSubmittingProject] = useState(false);
  const [projectError, setProjectError] = useState('');

  // RBAC permission state
  const [canEditProject, setCanEditProject] = useState(false);
  const [canViewProject, setCanViewProject] = useState(true);
  const [loadingPermissions, setLoadingPermissions] = useState(false);

  // Refresh state
  const [lastFetchedTime, setLastFetchedTime] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Top-level tab state
  const [topLevelTab, setTopLevelTab] = useState(1); // 0: Dashboard, 1: Applications
  const [showApplicationDetail, setShowApplicationDetail] = useState(false);

  // Table view state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'processing'>('all');
  const [currentPage, setCurrentPage] = useState(1);

  // Application details state
  const [activeTab, setActiveTab] = useState<'summary' | 'documents' | 'timeline'>('summary');

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  // Dashboard data state
  const [dashboardData, setDashboardData] = useState({
    totalApplications: 0,
    pendingApplications: 0,
    approvedApplications: 0,
    rejectedApplications: 0,
    totalLoanAmount: 0,
    averageProcessingTime: 0,
    applicationsByType: [] as { type: string, count: number }[],
    recentApplications: [] as Application[]
  });

  // Initialize data from API
  useEffect(() => {
    fetchProjects();
  }, []);

  // Initialize Google Drive service
  useEffect(() => {
    googleDriveService.current = new GoogleDriveService({
      onAuthStateChange: (authState) => {
        setGoogleDriveAuthState(authState);
        if (authState.isAuthenticated && authState.sessionId) {
          localStorage.setItem('googleDriveSessionId', authState.sessionId);
        }
      },
      onFilesLoaded: (files) => {
        setGoogleDriveFiles(files);
        setIsLoadingGoogleDriveFiles(false);
      },
      onError: (error) => {
        setGoogleDriveError(error);
        setError(error);
      },
      onSuccess: (message) => {
        setGoogleDriveSuccess(message);
        setSuccess(message);
      },
      onDownloadProgress: (progress) => {
        setGoogleDriveDownloadProgress(progress);
      },
      onLoadingStateChange: (loading) => {
        setIsLoadingGoogleDriveFiles(loading);
      },
      onFileInfoLoaded: (fileInfo) => {
        // Handle file info loaded
      },
      onFilePreviewLoaded: (previewInfo) => {
        // Handle file preview loaded
      },
      onFolderBreadcrumbLoaded: (breadcrumb) => {
        setFolderBreadcrumb(breadcrumb);
      },
      onFolderTreeLoaded: (tree) => {
        setFolderTree(tree);
        setIsLoadingFolderTree(false);
      }
    });

    // Check for existing session
    const checkExistingSession = async () => {
      const existingSessionId = localStorage.getItem('googleDriveSessionId');
      if (existingSessionId && googleDriveService.current) {
        try {
          const isAuthenticated = await googleDriveService.current.checkAuthStatus(existingSessionId);
          if (isAuthenticated) {
            setGoogleDriveAuthState({
              isAuthenticated: true,
              sessionId: existingSessionId
            });
          }
        } catch (error) {
          console.error('Error checking existing session:', error);
        }
      }
    };

    checkExistingSession();

    return () => {
      if (googleDriveService.current) {
        googleDriveService.current.destroy();
      }
    };
  }, []);

  // Fetch applications when selectedProject changes
  useEffect(() => {
    if (selectedProject) {
      fetchApplications();
    }
  }, [selectedProject]);

  // RBAC: Fetch project permissions when project changes
  useEffect(() => {
    if (selectedProject) {
      fetchProjectPermissions();
    }
  }, [selectedProject]);

  // Fetch customer documents when selectedApplication changes
  useEffect(() => {
    if (selectedApplication && selectedProject) {
      const fetchDocs = async () => {
        setLoadingDocuments(true);
        try {
          // Extract entityReference from the application's documents array
          let entityReference = selectedApplication.id; // fallback to application ID

          if (selectedApplication.documents && selectedApplication.documents.length > 0) {
            const firstDoc = selectedApplication.documents[0];
            if (firstDoc.extractedData && firstDoc.extractedData.entityReference) {
              entityReference = firstDoc.extractedData.entityReference;
            }
          }

          console.log('🔍 Auto-fetch using entityReference:', entityReference);

          const documents = await fetchCustomerDocuments(entityReference);
          setCustomerDocuments(documents);
        } catch (error) {
          console.error('Error fetching customer documents:', error);
          setCustomerDocuments([]);
        } finally {
          setLoadingDocuments(false);
        }
      };

      fetchDocs();
    }
  }, [selectedApplication, selectedProject]);

  // Update suggested questions based on context
  useEffect(() => {
    if (selectedDocument) {
      setSuggestedQuestions([
        { text: `Analyze this ${selectedDocument.entries?.find((e: any) => e.key === 'documentType')?.value || 'document'}`, icon: <TrendingUpIcon sx={{ fontSize: 16 }} /> },
        { text: "What are the key details?", icon: <ShowChartIcon sx={{ fontSize: 16 }} /> },
        { text: "Summarize the main information", icon: <DescriptionIcon sx={{ fontSize: 16 }} /> },
      ]);
    } else if (selectedApplication) {
      setSuggestedQuestions([
        { text: "Analyze this loan application", icon: <TrendingUpIcon sx={{ fontSize: 16 }} /> },
        { text: "What's the risk assessment?", icon: <ShowChartIcon sx={{ fontSize: 16 }} /> },
        { text: "Review the application status", icon: <AssignmentIcon sx={{ fontSize: 16 }} /> },
      ]);
    } else {
      setSuggestedQuestions([
        { text: "How many applications are there?", icon: <TrendingUpIcon sx={{ fontSize: 16 }} /> },
        { text: "What's the overall status?", icon: <ShowChartIcon sx={{ fontSize: 16 }} /> },
      ]);
    }
  }, [selectedDocument, selectedApplication]);

  // Removed sample data. Component relies only on real API responses.

  // API Integration Functions
  const fetchProjects = async () => {
    setIsLoadingProjects(true);
    try {
      const response = await axios.post(
        `${environment.apiUrl}/workspace/getProjectForOrg`,
        {
          data: {
            org_id: localStorage.getItem('org_id'),
            project_type: ["EKYC_DOCU"]
          },
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data && Array.isArray(response.data)) {
        // Transform API response to match our Project interface
        const apiProjects = response.data.map((project: any) => ({
          id: project.id,
          name: project.project_name,
          description: project.project_description,
          createdAt: project.last_updated_date || new Date().toISOString(),
          applicationsCount: 0 // Will be updated when we fetch applications
        }));

        setProjects(apiProjects);

        // Always select the first project (0th index) as default
        if (apiProjects.length > 0) {
          setSelectedProject(apiProjects[0]);
        }
      } else {
        // No projects returned
        console.warn('⚠️ No projects returned by API');
        setProjects([]);
        setSelectedProject(null);
        setError('No projects found.');
      }
    } catch (error: any) {
      console.error('Error fetching projects:', error);
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
        return;
      }
      setProjects([]);
      setSelectedProject(null);
      setError('Failed to fetch projects.');
    } finally {
      setIsLoadingProjects(false);
    }
  };

  // RBAC: Fetch project permissions
  const fetchProjectPermissions = async () => {
    if (!selectedProject) return;

    const userId = localStorage.getItem('user_email') || 'demouser01@bahwancybertek.com';
    const organisationId = localStorage.getItem('org_id') || 'bctglobal';

    setLoadingPermissions(true);

    try {
      // Call the correct RBAC endpoint: /analytics/rbac/users/{userId}/project-permissions/{projectId}
      const response = await fetch(
        `${environment.apiUrl}/analytics/rbac/users/${userId}/project-permissions/${selectedProject.id}?organisationId=${organisationId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('🔐 RBAC permissions response:', data);

        // Check if user can view this project
        const canView = data.permissions?.canView || false;
        const canEdit = data.permissions?.canEdit || false;

        setCanViewProject(canView);
        setCanEditProject(canEdit);
        console.log('🔐 User can view project:', canView, 'can edit project:', canEdit);
      } else {
        console.warn('⚠️ Failed to fetch permissions:', response.status, response.statusText);
        setCanViewProject(false);
        setCanEditProject(false);
      }
    } catch (error) {
      console.error('❌ Error fetching project permissions:', error);
      setCanViewProject(false);
      setCanEditProject(false);
    } finally {
      setLoadingPermissions(false);
    }
  };

  const handleCreateProject = async () => {
    if (!newProject.project_name.trim()) {
      setProjectError('Project name is required');
      return;
    }

    setIsSubmittingProject(true);
    setProjectError('');

    try {
      const response = await axios.post(
        `${environment.apiUrl}/workspace/updateCustomProjectForOrg`,
        {
          data: {
            org_id: localStorage.getItem("org_id"),
            source: "10_org",
            project_items: [
              {
                project_name: newProject.project_name,
                project_description: newProject.project_description,
                project_type: newProject.project_type || 'EKYC_DOCU',
                configuration: {
                  period_start_date: newProject.period_start_date,
                  period_end_date: newProject.period_end_date,
                  period_type: newProject.period_type || 'D'
                }
              }
            ]
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.upsertRef && response.data.upsertRef.length > 0) {
        // Fetch project details
        const projectDetails = await axios.post(
          `${environment.apiUrl}/workspace/getProjectDetails`,
          {
            data: {
              id: response.data.upsertRef[0]
            }
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            },
          }
        );

        setProjects(prev => [...prev, projectDetails.data]);
        setSelectedProject(projectDetails.data);
        setIsProjectDialogOpen(false);
        setSuccess('Project created successfully!');

        // Reset form
        setNewProject({
          project_name: '',
          project_description: '',
          project_type: 'EKYC_DOCU',
          period_start_date: new Date().toISOString().split('T')[0],
          period_end_date: new Date().toISOString().split('T')[0],
          period_type: 'monthly',
          source: 'manual',
          active: true,
          status: 'active'
        });

        // Refresh projects list
        await fetchProjects();

        // Auto-assign permissions for the new project
        try {
          const autoAssignResponse = await axios.post(
            `${environment.apiUrl}/manage/project-permissions/auto-assign`,
            {
              projectId: projectDetails.data.id,
              userId: localStorage.getItem('user_email') || 'admin@example.com',
              organisationId: localStorage.getItem('org_id') || 'org123'
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json',
              },
            }
          );

          console.log('✅ Auto-assign permissions successful:', autoAssignResponse.data);

          // Refresh project permissions after auto-assign
          await fetchProjectPermissions();

        } catch (autoAssignError: any) {
          console.error('⚠️ Auto-assign permissions failed:', autoAssignError);
          // Don't show error to user as project creation was successful
          // Auto-assign is a convenience feature, not critical
        }
      } else {
        setProjectError('Failed to create project');
      }
    } catch (error: any) {
      console.error('Error creating project:', error);
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
        return;
      }
      setProjectError(error?.response?.data?.message || 'Failed to create project');
    } finally {
      setIsSubmittingProject(false);
    }
  };

  const fetchApplications = async () => {
    if (!selectedProject?.id) return;

    setLoading(true);
    try {
      // Query customer profiles using the correct API
      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            topic: "",
            keywords: [],
            contentType: "EKYC_DOCS",
            pageSize: 100,
            pageNumber: 1,
            campaignId: selectedProject.id
          },
          agent: {
            code: "QUERY_RELEVANT_CUSTOMER_PROFILES"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data && response.data.data) {
        console.log('📊 Raw API response:', response.data);

        // Transform API response to match our Application interface
        const apiApplications = response.data.data.map((item: any, index: number) => {
          // Extract customer data from the API response based on actual field names
          const entries = item.entries || [];

          // Helper function to find entry value by key
          const getEntryValue = (key: string) => {
            const entry = entries.find((e: any) => e.key === key);
            return entry?.value || null;
          };

          // Extract personal information from actual API fields
          const personalInfo = {
            name: getEntryValue('fullName') || getEntryValue('accountEntityName') || `Customer ${index + 1}`,
            panCard: getEntryValue('panCard') || getEntryValue('pan') || 'N/A',
            aadharCard: getEntryValue('aadharCard') || getEntryValue('aadhar') || 'N/A',
            email: getEntryValue('email') || 'N/A',
            phone: getEntryValue('phone') || getEntryValue('mobile') || 'N/A'
          };

          // Extract financial information from actual API fields
          const financialInfo = {
            annualIncome: getEntryValue('annualIncome') || getEntryValue('income') || getEntryValue('salary') || 'N/A',
            employmentType: getEntryValue('employmentType') || getEntryValue('employment') || getEntryValue('jobType') || 'N/A',
            creditScore: getEntryValue('creditScore') || getEntryValue('credit_score') || 'N/A'
          };

          // Extract loan details from actual API fields
          const loanDetails = {
            requestedAmount: getEntryValue('loanAmount') || getEntryValue('loan_amount') || getEntryValue('requestedAmount') || 'N/A',
            propertyValue: getEntryValue('propertyValue') || getEntryValue('property_value') || getEntryValue('assetValue') || 'N/A',
            downPayment: getEntryValue('downPayment') || getEntryValue('down_payment') || 'N/A'
          };

          // Status will come from API when available - for now set to unknown
          const status: Application['status'] = 'unknown';

          // Create application object with mapped data
          const application = {
            id: item.docUID || `app-${index + 1}`,
            city: getEntryValue('city'),
            state: getEntryValue('state'),
            projectId: selectedProject.id,
            name: personalInfo.name && personalInfo.name !== 'N/A' && personalInfo.name !== ''
              ? `${personalInfo.name}`
              : `Loan Application ${index + 1}`,
            status: status,
            createdAt: new Date().toISOString(),
            documentsCount: 1,
            extractedData: {
              personalInfo,
              financialInfo,
              loanDetails
            },
            documents: [
              {
                id: item.docUID || `doc-${index + 1}`,
                name: getEntryValue('documentTitle') || 'Customer Profile Document',
                type: 'pdf',
                category: 'other' as const,
                url: '#',
                extractedData: {
                  docUID: item.docUID,
                  entityReference: getEntryValue('entityReference'), // This should now contain "CUST_000006" etc.
                  campaignId: getEntryValue('campaignId'),
                  informationCategory: getEntryValue('informationCategory'),
                  periodStartDate: getEntryValue('periodStartDate'),
                  periodEndDate: getEntryValue('periodEndate'),
                  bankName: getEntryValue('bankName'),
                  accountType: getEntryValue('accountType')
                },
                uploadedAt: new Date().toISOString()
              }
            ],
            // Add dummy data for enhanced UI
            dummyData: generateDummyData(index, getEntryValue('city'), getEntryValue('state'))
          };

          console.log(`📋 Mapped application ${index + 1}:`, application);
          console.log(`🔍 EntityReference for ${index + 1}:`, getEntryValue('entityReference'));
          return application;
        });

        console.log('✅ Transformed applications:', apiApplications);
        setApplications(apiApplications);
        updateDashboardData(apiApplications);
        setLastFetchedTime(new Date());
      } else {
        console.warn('⚠️ No applications in API response');
        setApplications([]);
        updateDashboardData([]);
        setError('No applications found for the selected project.');
      }
    } catch (error: any) {
      console.error('❌ Error fetching applications:', error);
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
        return;
      }
      setApplications([]);
      updateDashboardData([]);
      setError('Failed to fetch applications.');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const updateDashboardData = (apps: Application[]) => {
    const totalApplications = apps.length;
    const approvedCount = apps.filter(app => app.status === 'completed').length;
    const pendingCount = apps.filter(app => app.status === 'processing' || app.status === 'draft').length;
    const rejectedCount = apps.filter(app => app.status === 'requires_review').length;
    const unknownCount = apps.filter(app => app.status === 'unknown').length;

    // Calculate total loan amount from actual data or use mock calculation
    const totalLoanAmount = apps.reduce((sum, app) => {
      const amountStr = app.extractedData?.loanDetails?.requestedAmount;
      if (amountStr && amountStr !== 'N/A') {
        // Try to extract numeric value from various formats
        const amount = parseFloat(amountStr.replace(/[^0-9.]/g, '') || '0');
        return sum + amount;
      }
      return sum;
    }, 0);

    // Determine application types based on actual data
    const applicationTypes = apps.reduce((types: { type: string, count: number }[], app) => {
      // Try to get account type from documents array or use a default
      const accountType = app.documents?.[0]?.extractedData?.accountType;
      let typeName = 'Business Loan'; // Default type

      if (accountType && accountType !== 'N/A' && accountType !== '') {
        if (accountType === 'Savings Bank Account') {
          typeName = 'Personal Loan';
        } else if (accountType === 'Account') {
          typeName = 'Home Loan';
        }
      }

      const existingType = types.find(t => t.type === typeName);
      if (existingType) {
        existingType.count++;
      } else {
        types.push({ type: typeName, count: 1 });
      }
      return types;
    }, []);

    // Fill in missing types with 0 count
    const allTypes = ['Home Loan', 'Personal Loan', 'Business Loan'];
    allTypes.forEach(typeName => {
      if (!applicationTypes.find(t => t.type === typeName)) {
        applicationTypes.push({ type: typeName, count: 0 });
      }
    });

    setDashboardData({
      totalApplications,
      pendingApplications: pendingCount,
      approvedApplications: approvedCount,
      rejectedApplications: rejectedCount,
      totalLoanAmount,
      averageProcessingTime: 15, // Mock data
      applicationsByType: applicationTypes,
      recentApplications: apps.slice(0, 5)
    });
  };

  // URL upload functionality (exact match from SurveyInsights.tsx)
  const handleUrlUpload = async () => {
    if (!fileUrl.trim()) {
      setUrlValidationError('Please enter a valid file URL');
      return;
    }

    setUrlValidationError('');
    setIsUrlValidating(true);
    setError('');

    try {
      // Basic URL validation
      const url = new URL(fileUrl);
      if (!url.protocol.startsWith('http')) {
        throw new Error('Invalid URL protocol');
      }

      setIsUrlValidating(false);
      setIsDownloading(true);
      setDownloadProgress(0);

      // Simulate download progress
      const progressInterval = setInterval(() => {
        setDownloadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // For now, we'll simulate the download process
      // In a real implementation, you would download the file from the URL
      setTimeout(() => {
        clearInterval(progressInterval);
        setDownloadProgress(100);
        setIsDownloading(false);

        // Simulate successful download
        setSuccess('File downloaded successfully. Please use the device upload option to upload the downloaded file.');
        setFileUrl('');
      }, 3000);

    } catch (error: any) {
      setIsUrlValidating(false);
      setIsDownloading(false);
      setDownloadProgress(0);
      setUrlValidationError('Please enter a valid URL');
    }
  };

  // File upload functionality - matching DocsInsights exactly
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length || !selectedApplication) return;

    setUploadLoading(true);
    const file = event.target.files[0];
    const formData = new FormData();
    formData.append('file', file);
    formData.append('token', `Bearer ${localStorage.getItem('token')}`);
    formData.append('type', 'docs');

    try {
      // Upload file
      const uploadResponse = await axios.post(
        `${environment.apiUrl}/account/uploadFileForOrg`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        }
      );

      if (uploadResponse.data.status === 'success') {
        console.log('📤 File uploaded successfully:', uploadResponse.data.path);

        // Extract entityReference from the application's documents
        let entityReference = null;

        if (selectedApplication.documents && selectedApplication.documents.length > 0) {
          const firstDoc = selectedApplication.documents[0];
          console.log('🔍 First document in application:', firstDoc);
          console.log('🔍 First document extractedData:', firstDoc.extractedData);

          if (firstDoc.extractedData && firstDoc.extractedData.entityReference) {
            entityReference = firstDoc.extractedData.entityReference;
            console.log('✅ Found entityReference from application documents:', entityReference);
          } else {
            console.warn('⚠️ No entityReference found in application documents');
            console.log('🔍 Available extractedData keys:', firstDoc.extractedData ? Object.keys(firstDoc.extractedData) : 'No extractedData');
          }
        } else {
          console.warn('⚠️ No documents found in selected application');
        }

        if (!entityReference) {
          console.error('❌ No entityReference available for document indexing');
          setError('Failed to get entity reference for document indexing');
          return;
        }

        console.log('🔍 Using entityReference for indexing:', entityReference);

        // Index the document with form data
        const indexPayload = {
          data: {
            filePath: uploadResponse.data.path,
            sourceName: "Self Upload",
            fileType: "docs",
            contentType: "EKYC_DOCS",
            campaignId: selectedProject?.id || '',
            entityReference: entityReference
          },
          agent: {
            code: "INDEX_CUSTOMER_DOCUMENT"
          }
        };

        console.log('🔍 Indexing document with payload:', indexPayload);

        const indexResponse = await axios.post(
          `${environment.controlplaneUrl}/agent/flow/execute`,
          indexPayload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            }
          }
        );

        console.log('✅ Document indexed successfully:', indexResponse.data);

        setSuccess('Document uploaded successfully, AI system is processing your document. Refresh the data in a few minutes to view the extracted data.');
        // Reset form data
        setUploadFormData({
          topic: '',
          keywords: []
        });
        // Close dialog
        setUploadDialogOpen(false);
        // Refresh applications
        fetchApplications();
      }
    } catch (err: any) {
      if (
        err?.response?.data?.error === 'jwt malformed' ||
        err?.response?.data?.error === 'jwt expired'
      ) {
        console.log(err?.response?.data?.error);
        navigate('/login');
      }
      setError('Failed to upload and index document');
      console.error('Error uploading document:', err);
    } finally {
      setUploadLoading(false);
    }
  };

  // New application creation with document upload
  const handleNewApplicationUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length || !selectedProject) return;

    setUploadLoading(true);
    const file = event.target.files[0];
    const formData = new FormData();
    formData.append('file', file);
    formData.append('token', `Bearer ${localStorage.getItem('token')}`);
    formData.append('type', 'docs');

    try {
      // Upload file
      const uploadResponse = await axios.post(
        `${environment.apiUrl}/account/uploadFileForOrg`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        }
      );

      if (uploadResponse.data.status === 'success') {
        console.log('📤 New application file uploaded successfully:', uploadResponse.data.path);

        // Generate proper customer entityReference format
        const customerId = Math.floor(Math.random() * 999999) + 1;
        const entityReference = `CUST_${customerId.toString().padStart(6, '0')}`;

        console.log('🔍 Generated entityReference for new application:', entityReference);

        // Create new application with document
        const newApplication = {
          id: `app-${Date.now()}`,
          projectId: selectedProject.id,
          name: newApplicationFormData.name && newApplicationFormData.name.trim() !== ''
            ? newApplicationFormData.name
            : `New Loan Application ${Date.now()}`,
          status: 'unknown' as const,
          createdAt: new Date().toISOString(),
          documentsCount: 1,
          extractedData: {
            personalInfo: { name: 'N/A', panCard: 'N/A', aadharCard: 'N/A', email: 'N/A', phone: 'N/A' },
            financialInfo: { annualIncome: 'N/A', employmentType: 'N/A', creditScore: 'N/A' },
            loanDetails: { requestedAmount: 'N/A', propertyValue: 'N/A', downPayment: 'N/A' }
          },
          documents: [
            {
              id: `doc-${Date.now()}`,
              name: file.name,
              type: file.type,
              category: 'other' as const,
              url: uploadResponse.data.path,
              extractedData: {
                entityReference: entityReference, // Use the generated customer reference
                campaignId: selectedProject.id
              },
              uploadedAt: new Date().toISOString()
            }
          ]
        };

        // Index the document (no entityReference for new applications)
        const indexPayload = {
          data: {
            filePath: uploadResponse.data.path,
            sourceName: "Self Upload",
            fileType: "docs",
            contentType: "EKYC_DOCS",
            campaignId: selectedProject.id,
            // entityReference: entityReference, // Not needed for new applications
            topic: newApplicationFormData.topic || '',
            keywords: newApplicationFormData.keywords || []
          },
          agent: {
            code: "INDEX_CUSTOMER_DOCUMENT"
          }
        };

        console.log('🔍 Indexing new application document with payload:', indexPayload);

        const indexResponse = await axios.post(
          `${environment.controlplaneUrl}/agent/flow/execute`,
          indexPayload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            }
          }
        );

        console.log('✅ New application document indexed successfully:', indexResponse.data);

        // Add new application to the list
        setApplications(prev => [newApplication, ...prev]);

        // Select the new application and fetch its documents
        await handleApplicationSelect(newApplication);

        setSuccess('Document uploaded successfully, AI system is processing your document. Refresh the data in a few minutes to view the extracted data.');

        // Reset form data
        setNewApplicationFormData({
          name: '',
          topic: '',
          keywords: []
        });

        // Close dialog
        setNewApplicationDialogOpen(false);

        // Update dashboard
        updateDashboardData([newApplication, ...applications]);
      }
    } catch (err: any) {
      if (
        err?.response?.data?.error === 'jwt malformed' ||
        err?.response?.data?.error === 'jwt expired'
      ) {
        console.log(err?.response?.data?.error);
        navigate('/login');
      }
      setError('Failed to create new application');
      console.error('Error creating application:', err);
    } finally {
      setUploadLoading(false);
    }
  };



  // Chat handling functions
  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    const userMessage: ChatMessage = {
      id: messages.length + 1,
      text: newMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setIsTyping(true);

    try {
      // Use real API for chatbot response with conditional docUID
      const payload: any = {
        data: {
          query: newMessage.trim(),
          campaignId: selectedProject?.id // Always send project ID if available
        },
        agent: {
          code: selectedApplication ? "QUERY_CUSTOMER_DOCUMENT_INTEL_AND_RESPOND" : "QUERY_CUSTOMER_PROFILES_AND_RESPOND"
        }
      };

      // Priority: selectedDocument > selectedApplication > none
      if (selectedDocument) {
        // Try multiple possible UUID keys and locations
        let documentUuid = null;

        // Method 1: Look in document entries for 'uuid'
        if (selectedDocument.entries && Array.isArray(selectedDocument.entries)) {
          const uuidEntry = selectedDocument.entries.find((e: any) =>
            e.key === 'uuid' || e.key === 'documentId' || e.key === 'documentUuid'
          );
          if (uuidEntry?.value) {
            documentUuid = uuidEntry.value;
          }
        }

        // Method 2: Look for UUID in document object directly
        if (!documentUuid) {
          documentUuid = selectedDocument.uuid || selectedDocument.documentId || selectedDocument.id || selectedDocument.documentUuid;
        }

        // Method 3: Look for UUID in nested properties
        if (!documentUuid && selectedDocument.data) {
          documentUuid = selectedDocument.data.uuid || selectedDocument.data.documentId || selectedDocument.data.id;
        }

        if (documentUuid) {
          // If a specific document is selected, use its UUID as docUID
          payload.data.docUID = documentUuid;
          console.log('🔍 ✅ Using selected document UUID for chatbot:', documentUuid);
        } else {
          // Fallback to application ID if no UUID found
          payload.data.docUID = selectedApplication?.id;
          console.log('🔍 ⚠️ No document UUID found, using application ID:', selectedApplication?.id);
        }
      } else if (selectedApplication) {
        // Fallback to application ID if no document is selected
        payload.data.docUID = selectedApplication.id;
        console.log('🔍 📋 No document selected, using application ID for chatbot:', selectedApplication.id);
      }

      console.log('🤖 Chat API Request:', payload);

      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      console.log('🤖 Chat API Response:', response.data);

      let botResponseText = '';
      if (response && response.data && response.data.data && response.data.data.length > 0) {
        // Extract response from API based on objectType
        const responses = response.data.data;

        // Look for PARAGRAPH response first (most common)
        const paragraphResponse = responses.find((item: any) => item.objectType === 'PARAGRAPH');
        if (paragraphResponse && paragraphResponse.value) {
          botResponseText = paragraphResponse.value;
        } else if (responses[0].entries && responses[0].entries.length > 0) {
          // Handle DICTIONARY format
          botResponseText = responses[0].entries.map((entry: any) =>
            `**${entry.label}**: ${entry.value}`
          ).join('\n\n');
        } else {
          botResponseText = 'I found some information but it\'s not in a readable format. Please try rephrasing your question.';
        }
      } else {
        // Fallback response if API doesn't return data
        if (selectedApplication) {
          botResponseText = `Based on the loan application analysis for **${selectedApplication.name}**:\n\n• **Risk Assessment**: Moderate to Low Risk\n• **Documentation**: ${selectedApplication.documentsCount} documents reviewed\n• **Status**: ${selectedApplication.status.charAt(0).toUpperCase() + selectedApplication.status.slice(1)}\n• **Recommendation**: Proceed with standard verification process`;
        } else {
          botResponseText = 'I can help you with general loan processing questions. Please select a specific application from the list to get detailed analysis, or ask me about loan processing workflows, requirements, or general information.';
        }
      }

      const botResponse: ChatMessage = {
        id: messages.length + 2,
        text: botResponseText,
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botResponse]);

      // Parse the response to find follow-up questions similar to CustomerService
      const followupItem = response.data.data?.find((item: any) =>
        item.objectType === 'MULTITEXTOPTIONS'
      );

      if (followupItem) {
        const icons = [
          <TrendingUpIcon sx={{ fontSize: 16 }} />,
          <ShowChartIcon sx={{ fontSize: 16 }} />,
          <TrendingDownIcon sx={{ fontSize: 16 }} />,
          <MonetizationOnIcon sx={{ fontSize: 16 }} />,
          <SentimentSatisfiedIcon sx={{ fontSize: 16 }} />
        ];

        const newSuggestedQuestions = followupItem?.value?.map((question: string, index: number) => ({
          text: question,
          icon: icons[index % icons.length] as React.ReactElement
        })) || [];

        setSuggestedQuestions(newSuggestedQuestions);
      }
    } catch (error: any) {
      console.error('Error sending message:', error);

      // Handle JWT errors similar to CustomerService
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
        return;
      }

      // Fallback response on error
      const fallbackResponse: ChatMessage = {
        id: messages.length + 2,
        text: 'I\'m having trouble processing your request right now. Please try again or select an application for manual analysis.',
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, fallbackResponse]);
      setError('Failed to process your request. Please try again.');
    } finally {
      setIsTyping(false);
    }
  };

  // Format message content (can be enhanced for loan-specific formatting)
  const formatMessageContent = (message: ChatMessage) => {
    return (
      <Typography
        variant="body2"
        sx={{
          fontSize: '0.8rem',
          lineHeight: 1.4,
          whiteSpace: 'pre-wrap'
        }}
      >
        {message.text}
      </Typography>
    );
  };

  // Helper function for short titles
  const getShortTitle = (title: string) => {
    if (!title || title === 'N/A') return 'Document';
    if (title.length <= 60) return title;

    // Try to extract meaningful parts
    if (title.includes('Account Statement')) {
      const parts = title.split('Account Statement');
      return parts[0].trim() + ' Statement';
    }

    if (title.includes('Account Holder')) {
      const parts = title.split('Account Holder');
      return parts[0].trim() + ' Statement';
    }

    if (title.includes('Passport')) {
      return 'Passport Document';
    }

    if (title.includes('Bank Statement')) {
      return 'Bank Statement';
    }

    // Fallback: truncate to first 60 characters
    return title.substring(0, 60) + '...';
  };

  // Helper functions for application data
  const getStatusColor = (status: Application['status']) => {
    switch (status) {
      case 'completed':
        return '#4caf50';
      case 'processing':
        return '#ff9800';
      case 'unknown':
        return '#6b7280';
      default:
        return '#6b7280';
    }
  };

  const getStatusIcon = (status: Application['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon sx={{ color: '#4caf50' }} />;
      case 'processing':
        return <PendingActionsIcon sx={{ color: '#ff9800' }} />;
      case 'unknown':
        return <InfoIcon sx={{ color: '#6b7280' }} />;
      default:
        return <InfoIcon sx={{ color: '#6b7280' }} />;
    }
  };

  const getStatusDisplayName = (status: Application['status']) => {
    switch (status) {
      case 'processing':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'unknown':
        return 'Unknown';
      default:
        return String(status).replace('_', ' ').toUpperCase();
    }
  };

  // Tab rendering functions
  const renderSummaryTab = () => {
    if (!selectedApplication?.extractedData) {
      return (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <AccessTimeIcon sx={{ fontSize: 64, color: '#E5E9EF', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#64748B', mb: 1 }}>
            Processing in Progress
          </Typography>
          <Typography variant="body2" sx={{ color: '#94A3B8' }}>
            AI system is extracting key information from your documents.
          </Typography>
        </Box>
      );
    }

    // Helper function to mask sensitive data
    const maskSensitiveData = (value: string, type: string) => {
      if (!value || value === 'N/A') {
        // Return masked placeholder when value is N/A
        switch (type) {
          case 'PAN':
            return 'ABCD****EF';
          case 'Aadhar':
            return '1234****5678';
          case 'Income':
            return '₹******';
          case 'CreditScore':
            return '7**';
          case 'LoanAmount':
            return '₹******';
          case 'PropertyValue':
            return '₹******';
          case 'DownPayment':
            return '₹******';
          default:
            return '****';
        }
      }

      // If value exists, apply appropriate masking
      switch (type) {
        case 'PAN':
          // PAN format: ABCDE1234F -> ABC**1234F
          if (value.length >= 5) {
            return value.substring(0, 3) + '**' + value.substring(value.length - 5);
          }
          return '****' + value.substring(value.length - 4);

        case 'Aadhar':
          // Aadhar format: 123456789012 -> 1234****9012
          if (value.length >= 8) {
            return value.substring(0, 4) + '****' + value.substring(value.length - 4);
          }
          return '****' + value.substring(value.length - 4);

        case 'Income':
        case 'LoanAmount':
        case 'PropertyValue':
        case 'DownPayment':
          // Financial values: 500000 -> ₹5,00,000
          const numValue = parseFloat(value.replace(/[^0-9.]/g, ''));
          if (!isNaN(numValue)) {
            return '₹' + numValue.toLocaleString('en-IN');
          }
          return '₹******';


        default:
          return value;
      }
    };

    // Helper function to check if a value is actually available (not just a masked placeholder)
    const isValueAvailable = (value: string, type: string) => {
      if (!value || value === 'N/A' || value === 'Na' || value === null || value === '') {
        return false;
      }

      // Check if it's just a masked placeholder
      switch (type) {
        case 'PAN':
          return value !== 'ABCD****EF';
        case 'Aadhar':
          return value !== '1234****5678';
        case 'Income':
        case 'LoanAmount':
        case 'PropertyValue':
        case 'DownPayment':
          return value !== '₹******';
        case 'CreditScore':
          return value !== '7**';
        default:
          return true;
      }
    };

    const summaryData = [
      {
        label: 'Applicant Name',
        value: selectedApplication.extractedData.personalInfo.name,
        category: 'Personal',
        type: 'text'
      },
      {
        label: 'PAN Number',
        value: maskSensitiveData(selectedApplication.extractedData.personalInfo.panCard, 'PAN'),
        category: 'Personal',
        type: 'PAN',
        originalValue: selectedApplication.extractedData.personalInfo.panCard
      },
      {
        label: 'Aadhar Number',
        value: maskSensitiveData(selectedApplication.extractedData.personalInfo.aadharCard, 'Aadhar'),
        category: 'Personal',
        type: 'Aadhar',
        originalValue: selectedApplication.extractedData.personalInfo.aadharCard
      },
      {
        label: 'Annual Income',
        value: maskSensitiveData(selectedApplication.extractedData.financialInfo.annualIncome, 'Income'),
        category: 'Personal',
        type: 'Income',
        originalValue: selectedApplication.extractedData.financialInfo.annualIncome
      },
      {
        label: 'Employment Type',
        value: selectedApplication.extractedData.financialInfo.employmentType,
        category: 'Financial',
        type: 'text'
      },
      {
        label: 'Loan Amount Requested',
        value: maskSensitiveData(selectedApplication.extractedData.loanDetails.requestedAmount, 'LoanAmount'),
        category: 'Loan',
        type: 'LoanAmount',
        originalValue: selectedApplication.extractedData.loanDetails.requestedAmount
      },
      {
        label: 'Property Value',
        value: maskSensitiveData(selectedApplication.extractedData.loanDetails.propertyValue, 'PropertyValue'),
        category: 'Loan',
        type: 'PropertyValue',
        originalValue: selectedApplication.extractedData.loanDetails.propertyValue
      },
      {
        label: 'Down Payment',
        value: maskSensitiveData(selectedApplication.extractedData.loanDetails.downPayment, 'DownPayment'),
        category: 'Loan',
        type: 'DownPayment',
        originalValue: selectedApplication.extractedData.loanDetails.downPayment
      },
      {
        label: 'City',
        value: selectedApplication.city || '',
        category: 'Location',
        type: 'text'
      },
      {
        label: 'State',
        value: selectedApplication.state || '',
        category: 'Location',
        type: 'text'
      },
      {
        label: 'Date of Birth',
        value: '', // Will be populated from document data
        category: 'Personal',
        type: 'text',
        fromDocuments: true,
        documentKey: 'dateOfBirth'
      },
      {
        label: 'Identity Number',
        value: '', // Will be populated from document data
        category: 'Personal',
        type: 'text',
        fromDocuments: true,
        documentKey: 'identityNumber'
      },
      {
        label: 'Gross Total Income',
        value: '', // Will be populated from document data
        category: 'Financial',
        type: 'Income',
        fromDocuments: true,
        documentKey: 'grossTotalIncome'
      }
    ];

    // Helper function to get value from customer documents
    const getDocumentValue = (key: string) => {
      if (!customerDocuments || customerDocuments.length === 0) return null;

      for (const doc of customerDocuments) {
        const entry = doc.entries?.find((e: any) => e.key === key);
        if (entry && entry.value && entry.value !== 'N/A' && entry.value !== 'Na' && entry.value !== null && entry.value !== '') {
          return entry.value;
        }
      }
      return null;
    };

    // Populate document-based fields
    const populatedSummaryData = summaryData.map(item => {
      if (item.fromDocuments && item.documentKey) {
        const documentValue = getDocumentValue(item.documentKey);
        if (documentValue) {
          if (item.type === 'Income') {
            // Format income with Indian currency
            const numValue = parseInt(documentValue);
            if (!isNaN(numValue)) {
              return { ...item, value: `₹${numValue.toLocaleString('en-IN')}` };
            }
          }
          return { ...item, value: documentValue };
        }
      }
      return item;
    });

    // Filter out items that don't have available data
    const filteredSummaryData = populatedSummaryData.filter(item => {
      if (item.type === 'text') {
        return isValueAvailable(item.value || '', 'text');
      } else {
        return isValueAvailable(item.originalValue || item.value || '', item.type);
      }
    });

    // Get unique categories from filtered data
    const categories = [...new Set(filteredSummaryData.map(item => item.category))];

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {categories.map(category => (
          <Card key={category} sx={{ overflow: 'hidden' }}>
            <Box sx={{ backgroundColor: '#F9FAFB', px: 3, py: 2, borderBottom: '1px solid #E5E7EB' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
                {category} Information
              </Typography>
            </Box>
            <Box sx={{ overflowX: 'auto' }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: '0.75rem', textTransform: 'uppercase', width: '25%' }}>
                        FIELD
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: '0.75rem', textTransform: 'uppercase', width: '75%' }}>
                        VALUE
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSummaryData
                      .filter(item => item.category === category)
                      .map((item, index) => (
                        <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#F9FAFB' } }}>
                          <TableCell sx={{ fontWeight: 600, color: '#111827', fontSize: '0.875rem' }}>
                            {item.label}
                          </TableCell>
                          <TableCell sx={{ color: '#374151', fontSize: '0.875rem' }}>
                            {item.value}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Card>
        ))}
      </Box>
    );
  };

  const renderDocumentsTab = () => {
    // Helper function for short titles - defined at the top
    const getShortTitle = (title: string) => {
      if (!title || title === 'N/A') return 'Document';
      if (title.length <= 60) return title;

      // Try to extract meaningful parts
      if (title.includes('Account Statement')) {
        const parts = title.split('Account Statement');
        return parts[0].trim() + ' Statement';
      }

      if (title.includes('Account Holder')) {
        const parts = title.split('Account Holder');
        return parts[0].trim() + ' Statement';
      }

      if (title.includes('Passport')) {
        return 'Passport Document';
      }

      if (title.includes('Bank Statement')) {
        return 'Bank Statement';
      }

      // Fallback: truncate to first 60 characters
      return title.substring(0, 60) + '...';
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Processing Time Info */}


        {/* Customer Documents Section */}
        <Box sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#131F32' }}>
              Customer Documents ({customerDocuments.length})
            </Typography>
            {selectedDocument && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* <Chip
                  label={`Selected: ${getShortTitle(selectedDocument.entries?.find((e: any) => e.key === 'documentTitle')?.value || 'Document')}`}
                  size="small"
                  sx={{
                    backgroundColor: '#9C27B0',
                    color: 'white',
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }}
                /> */}
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    setSelectedDocument(null);
                    console.log('📄 Document selection cleared');
                  }}
                  sx={{
                    color: '#9C27B0',
                    fontSize: '0.75rem',
                    '&:hover': { backgroundColor: '#F3E5F5' }
                  }}
                >
                  Clear Selection
                </Button>
              </Box>
            )}
          </Box>

          {loadingDocuments ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} sx={{ color: '#9C27B0' }} />
            </Box>
          ) : customerDocuments.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {customerDocuments.map((document: any, index: number) => {
                // Extract document information from API response using helper function
                const getEntryValue = (key: string) => {
                  const entry = document.entries?.find((e: any) => e.key === key);
                  return entry?.value || 'N/A';
                };

                const documentTitle = getEntryValue('documentTitle');
                const documentType = getEntryValue('documentType');
                const documentCategory = getEntryValue('documentCategory');
                const sourceName = getEntryValue('sourceName');
                const sourceLink = getEntryValue('sourceLink');
                const fullName = getEntryValue('fullName') || getEntryValue('accountEntityName');
                const city = getEntryValue('city');
                const state = getEntryValue('state');
                const bankName = getEntryValue('bankName');
                const periodStartDate = getEntryValue('periodStartDate');
                const periodEndDate = getEntryValue('periodEndate');
                const dateOfBirth = getEntryValue('dateOfBirth');
                const documentUuid = getEntryValue('uuid'); // Extract UUID from entries

                // Create a shorter display title for better UI
                const getShortTitle = (title: string) => {
                  if (title.length <= 60) return title;

                  // Try to extract meaningful parts
                  if (title.includes('Account Statement')) {
                    const parts = title.split('Account Statement');
                    return parts[0].trim() + ' Statement';
                  }

                  if (title.includes('Account Holder')) {
                    const parts = title.split('Account Holder');
                    return parts[0].trim() + ' Statement';
                  }

                  // Fallback: truncate to first 60 characters
                  return title.substring(0, 60) + '...';
                };

                const displayTitle = getShortTitle(documentTitle);

                return (
                  <StyledAccordion
                    key={documentUuid || index}
                    expanded={expandedDocument === documentUuid}
                    onChange={() => {
                      // Toggle expansion - if already expanded, collapse; otherwise expand this one
                      if (expandedDocument === documentUuid) {
                        setExpandedDocument(null);
                      } else {
                        setExpandedDocument(documentUuid);
                      }
                    }}
                    sx={{
                      width: '100%',
                      border: '1px solid #E5E9EF',
                      backgroundColor: expandedDocument === documentUuid ? '#F8F4FF' : '#FFFFFF',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#9C27B0'
                      },
                      '&.Mui-expanded': {
                        backgroundColor: '#F8F4FF',
                        borderColor: '#9C27B0',
                        boxShadow: '0 4px 6px rgba(156, 39, 176, 0.08)'
                      }
                    }}
                    onClick={(e) => {
                      // Prevent accordion expansion when clicking for selection
                      e.stopPropagation();

                      // If clicking on already selected document, deselect it
                      if (selectedDocument && selectedDocument.entries?.find((e: any) => e.key === 'uuid')?.value === documentUuid) {
                        setSelectedDocument(null);
                        setChatOpen(false); // Collapse chatbot when deselecting document
                        console.log('📄 Document deselected');

                        // Clear chatbot messages when deselecting document
                        setMessages([
                          {
                            id: 1,
                            text: "Hello! I'm your AI assistant. How can I help you analyze loan applications?",
                            sender: 'bot',
                            timestamp: new Date(),
                          },
                        ]);
                        setNewMessage('');
                        setChatResponse('');
                      } else {
                        // Select the new document (this will automatically deselect the previous one)
                        setSelectedDocument(document);
                        console.log('📄 Document selected for chatbot context:', documentUuid);
                        console.log('📄 Full document object:', document);
                        console.log('📄 Document entries:', document.entries);

                        // Clear chatbot messages when selecting a new document
                        setMessages([
                          {
                            id: 1,
                            text: `Hello! I'm your AI assistant. I'm now analyzing the document: "${displayTitle}". How can I help you with this document?`,
                            sender: 'bot',
                            timestamp: new Date(),
                          },
                        ]);
                        setNewMessage('');
                        setChatResponse('');
                      }
                    }}
                  >
                    <StyledAccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        backgroundColor: expandedDocument === documentUuid ? '#F8F4FF' : 'transparent',
                        transition: 'all 0.2s ease',
                        '&.Mui-expanded': {
                          backgroundColor: '#F8F4FF',
                          borderBottom: '1px solid #F3E5F5'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Box sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: expandedDocument === documentUuid ? '#F3E5F5' : '#F8F4FF',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'all 0.2s ease'
                        }}>
                          <DescriptionIcon sx={{
                            color: expandedDocument === documentUuid ? '#9C27B0' : '#BA68C8',
                            fontSize: 20
                          }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" sx={{
                              fontWeight: 600,
                              color: '#131F32',
                              mb: 0.5,
                              fontSize: '1rem',
                              lineHeight: 1.3
                            }}>
                              {displayTitle}
                            </Typography>
                            {/* {selectedDocument && selectedDocument.entries?.find((e: any) => e.key === 'uuid')?.value === documentUuid && (
                            <Chip
                              label="Selected"
                              size="small"
                              sx={{
                                backgroundColor: '#9C27B0',
                                color: 'white',
                                fontSize: '0.625rem',
                                height: '18px',
                                fontWeight: 600
                              }}
                            />
                          )} */}
                          </Box>

                          {/* Document Type and Source Tags */}
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {documentType && documentType !== 'N/A' && (
                              <Chip
                                label={documentType.replace('_', ' ')}
                                size="small"
                                sx={{
                                  backgroundColor: '#E8F5E8',
                                  color: '#2E7D32',
                                  fontSize: '0.625rem',
                                  height: '20px',
                                  fontWeight: 600
                                }}
                              />
                            )}
                            {sourceName && sourceName !== 'N/A' && (
                              <Chip
                                label={sourceName}
                                size="small"
                                sx={{
                                  backgroundColor: '#E3F2FD',
                                  color: '#1976D2',
                                  fontSize: '0.625rem',
                                  height: '20px',
                                  fontWeight: 600
                                }}
                              />
                            )}
                          </Box>
                        </Box>

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 1, marginRight: '20px' }}>
                          {/* Chat Button */}
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ChatIcon sx={{ fontSize: 16 }} />}
                            onClick={(e) => {
                              // e.stopPropagation();
                              setSelectedDocument(document);
                              setChatOpen(true);
                              console.log('📄 Document selected for chat:', documentUuid);
                            }}
                            sx={{
                              borderColor: '#9C27B0',
                              color: '#9C27B0',
                              fontSize: '0.75rem',
                              height: '32px',
                              minWidth: 'auto',
                              px: 1.5,
                              '&:hover': {
                                backgroundColor: '#F3E5F5',
                                borderColor: '#7B1FA2'
                              }
                            }}
                          >
                            Chat
                          </Button>

                          {/* View Document Button */}
                          {sourceLink && sourceLink !== 'N/A' && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(sourceLink, '_blank');
                              }}
                              sx={{
                                borderColor: '#6b7280',
                                color: '#6b7280',
                                fontSize: '0.75rem',
                                height: '32px',
                                minWidth: 'auto',
                                px: 1.5,
                                '&:hover': {
                                  backgroundColor: '#f3f4f6',
                                  borderColor: '#4b5563'
                                }
                              }}
                            >
                              View
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </StyledAccordionSummary>

                    <StyledAccordionDetails>
                      {/* Extracted Information Section - Matching Reference Image Layout */}
                      <Box sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{
                          fontWeight: 600,
                          color: '#1a1a1a',
                          mb: 3,
                          fontSize: '1.125rem'
                        }}>
                          Extracted Information
                        </Typography>

                        {/* 2-Column Grid Layout - Matching Reference Image */}
                        <Grid container spacing={3}>
                          {/* Column 1 - Left Side (Fields 1, 3, 5, 7) */}
                          <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              {/* Field 1 */}
                              {fullName && fullName !== 'N/A' && fullName !== 'Na' && fullName !== null && fullName !== '' && (
                                <Box sx={{
                                  backgroundColor: '#FFFFFF',
                                  borderRadius: '12px',
                                  p: 2.5,
                                  border: '1px solid #E5E7EB',
                                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                    borderColor: '#9C27B0'
                                  }
                                }}>
                                  <Typography variant="caption" sx={{
                                    color: '#6B7280',
                                    textTransform: 'uppercase',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    display: 'block',
                                    mb: 1,
                                    letterSpacing: '0.05em'
                                  }}>
                                    Full Name
                                  </Typography>
                                  <Typography variant="body1" sx={{
                                    fontWeight: 600,
                                    color: '#111827',
                                    fontSize: '1rem',
                                    lineHeight: 1.4
                                  }}>
                                    {fullName}
                                  </Typography>
                                </Box>
                              )}

                              {/* Field 3 */}
                              {dateOfBirth && dateOfBirth !== 'N/A' && dateOfBirth !== 'Na' && dateOfBirth !== null && dateOfBirth !== '' && (
                                <Box sx={{
                                  backgroundColor: '#FFFFFF',
                                  borderRadius: '12px',
                                  p: 2.5,
                                  border: '1px solid #E5E7EB',
                                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                    borderColor: '#9C27B0'
                                  }
                                }}>
                                  <Typography variant="caption" sx={{
                                    color: '#6B7280',
                                    textTransform: 'uppercase',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    display: 'block',
                                    mb: 1,
                                    letterSpacing: '0.05em'
                                  }}>
                                    Date of Birth
                                  </Typography>
                                  <Typography variant="body1" sx={{
                                    fontWeight: 600,
                                    color: '#111827',
                                    fontSize: '1rem',
                                    lineHeight: 1.4
                                  }}>
                                    {dateOfBirth}
                                  </Typography>
                                </Box>
                              )}

                              {/* Field 5 */}
                              {getEntryValue('accountEntityName') && getEntryValue('accountEntityName') !== 'N/A' && getEntryValue('accountEntityName') !== 'Na' && getEntryValue('accountEntityName') !== null && getEntryValue('accountEntityName') !== '' && (
                                <Box sx={{
                                  backgroundColor: '#FFFFFF',
                                  borderRadius: '12px',
                                  p: 2.5,
                                  border: '1px solid #E5E7EB',
                                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                    borderColor: '#9C27B0'
                                  }
                                }}>
                                  <Typography variant="caption" sx={{
                                    color: '#6B7280',
                                    textTransform: 'uppercase',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    display: 'block',
                                    mb: 1,
                                    letterSpacing: '0.05em'
                                  }}>
                                    Account Entity Name
                                  </Typography>
                                  <Typography variant="body1" sx={{
                                    fontWeight: 600,
                                    color: '#111827',
                                    fontSize: '1rem',
                                    lineHeight: 1.4
                                  }}>
                                    {getEntryValue('accountEntityName')}
                                  </Typography>
                                </Box>
                              )}

                              {/* Field 7 */}
                              {periodStartDate && periodStartDate !== 'N/A' && periodStartDate !== 'Na' && periodStartDate !== null && periodStartDate !== '' && (
                                <Box sx={{
                                  backgroundColor: '#FFFFFF',
                                  borderRadius: '12px',
                                  p: 2.5,
                                  border: '1px solid #E5E7EB',
                                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                    borderColor: '#9C27B0'
                                  }
                                }}>
                                  <Typography variant="caption" sx={{
                                    color: '#6B7280',
                                    textTransform: 'uppercase',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    display: 'block',
                                    mb: 1,
                                    letterSpacing: '0.05em'
                                  }}>
                                    Period Start Date
                                  </Typography>
                                  <Typography variant="body1" sx={{
                                    fontWeight: 600,
                                    color: '#111827',
                                    fontSize: '1rem',
                                    lineHeight: 1.4
                                  }}>
                                    {periodStartDate}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Grid>

                          {/* Column 2 - Right Side (Fields 2, 4, 6, 8) */}
                          <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              {/* Field 2 */}
                              {getEntryValue('identityNumber') && getEntryValue('identityNumber') !== 'N/A' && getEntryValue('identityNumber') !== 'Na' && getEntryValue('identityNumber') !== null && getEntryValue('identityNumber') !== '' && (
                                <Box sx={{
                                  backgroundColor: '#FFFFFF',
                                  borderRadius: '12px',
                                  p: 2.5,
                                  border: '1px solid #E5E7EB',
                                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                    borderColor: '#9C27B0'
                                  }
                                }}>
                                  <Typography variant="caption" sx={{
                                    color: '#6B7280',
                                    textTransform: 'uppercase',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    display: 'block',
                                    mb: 1,
                                    letterSpacing: '0.05em'
                                  }}>
                                    Identity Number
                                  </Typography>
                                  <Typography variant="body1" sx={{
                                    fontWeight: 600,
                                    color: '#111827',
                                    fontSize: '1rem',
                                    lineHeight: 1.4
                                  }}>
                                    {getEntryValue('identityNumber')}
                                  </Typography>
                                </Box>
                              )}

                              {/* Field 4 */}
                              {getEntryValue('fullAddress') && getEntryValue('fullAddress') !== 'N/A' && getEntryValue('fullAddress') !== 'Na' && getEntryValue('fullAddress') !== null && getEntryValue('fullAddress') !== '' && (
                                <Box sx={{
                                  backgroundColor: '#FFFFFF',
                                  borderRadius: '12px',
                                  p: 2.5,
                                  border: '1px solid #E5E7EB',
                                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                    borderColor: '#9C27B0'
                                  }
                                }}>
                                  <Typography variant="caption" sx={{
                                    color: '#6B7280',
                                    textTransform: 'uppercase',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    display: 'block',
                                    mb: 1,
                                    letterSpacing: '0.05em'
                                  }}>
                                    Full Address
                                  </Typography>
                                  <Typography variant="body1" sx={{
                                    fontWeight: 600,
                                    color: '#111827',
                                    fontSize: '1rem',
                                    lineHeight: 1.4
                                  }}>
                                    {getEntryValue('fullAddress')}
                                  </Typography>
                                </Box>
                              )}

                              {/* Field 6 */}
                              {getEntryValue('bankName') && getEntryValue('bankName') !== 'N/A' && getEntryValue('bankName') !== 'Na' && getEntryValue('bankName') !== null && getEntryValue('bankName') !== '' && (
                                <Box sx={{
                                  backgroundColor: '#FFFFFF',
                                  borderRadius: '12px',
                                  p: 2.5,
                                  border: '1px solid #E5E7EB',
                                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                    borderColor: '#9C27B0'
                                  }
                                }}>
                                  <Typography variant="caption" sx={{
                                    color: '#6B7280',
                                    textTransform: 'uppercase',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    display: 'block',
                                    mb: 1,
                                    letterSpacing: '0.05em'
                                  }}>
                                    Bank Name
                                  </Typography>
                                  <Typography variant="body1" sx={{
                                    fontWeight: 600,
                                    color: '#111827',
                                    fontSize: '1rem',
                                    lineHeight: 1.4
                                  }}>
                                    {getEntryValue('bankName')}
                                  </Typography>
                                </Box>
                              )}

                              {/* Field 8 */}
                              {periodEndDate && periodEndDate !== 'N/A' && periodEndDate !== 'Na' && periodEndDate !== null && periodEndDate !== '' && (
                                <Box sx={{
                                  backgroundColor: '#FFFFFF',
                                  borderRadius: '12px',
                                  p: 2.5,
                                  border: '1px solid #E5E7EB',
                                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                    borderColor: '#9C27B0'
                                  }
                                }}>
                                  <Typography variant="caption" sx={{
                                    color: '#6B7280',
                                    textTransform: 'uppercase',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    display: 'block',
                                    mb: 1,
                                    letterSpacing: '0.05em'
                                  }}>
                                    Period End Date
                                  </Typography>
                                  <Typography variant="body1" sx={{
                                    fontWeight: 600,
                                    color: '#111827',
                                    fontSize: '1rem',
                                    lineHeight: 1.4
                                  }}>
                                    {periodEndDate}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>

                    </StyledAccordionDetails>
                  </StyledAccordion>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <DescriptionIcon sx={{ fontSize: 48, color: '#E5E9EF', mb: 2 }} />
              <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                No customer documents found for this application
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const renderTimelineTab = () => {
    const timelineEvents = [
      {
        id: '1',
        title: 'Application Created',
        description: 'New loan application created with initial documents',
        timestamp: new Date(selectedApplication?.createdAt || Date.now()),
        status: 'completed',
        icon: <DescriptionIcon sx={{ fontSize: 16, color: 'white' }} />
      },
      {
        id: '2',
        title: 'Documents Uploaded',
        description: `${customerDocuments?.length ?? 0} ${customerDocuments?.length === 1 ? "document" : "documents"} uploaded successfully`,
        timestamp: new Date(((selectedApplication?.createdAt ? new Date(selectedApplication.createdAt).getTime() : Date.now()) + 5 * 60000)),
        status: 'completed',
        icon: <DescriptionIcon sx={{ fontSize: 16, color: 'white' }} />
      },
      {
        id: '3',
        title: 'AI Processing Started',
        description: 'AI agent started processing documents for categorization and data extraction',
        timestamp: new Date((selectedApplication?.createdAt ? new Date(selectedApplication.createdAt).getTime() : Date.now()) + 10 * 60000),
        status: 'completed',
        icon: <AccessTimeIcon sx={{ fontSize: 16, color: 'white' }} />
      },
      {
        id: '4',
        title: 'Data Extraction Completed',
        description: 'AI agent completed extracting key information from all documents',
        timestamp: new Date((selectedApplication?.createdAt ? new Date(selectedApplication.createdAt).getTime() : Date.now()) + 15 * 60000),
        status: 'completed',
        icon: <CheckCircleIcon sx={{ fontSize: 16, color: 'white' }} />
      }
    ];

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {timelineEvents.map((event, eventIdx) => (
          <Box key={event.id} sx={{ position: 'relative' }}>
            {eventIdx !== timelineEvents.length - 1 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 32,
                  left: 16,
                  width: '2px',
                  height: 'calc(100% - 32px)',
                  backgroundColor: '#E5E7EB'
                }}
              />
            )}
            <Box sx={{ display: 'flex', gap: 3, pb: 3 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  backgroundColor: event.status === 'completed' ? '#10B981' : event.status === 'processing' ? '#F59E0B' : '#6B7280',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1,
                  position: 'relative'
                }}
              >
                {event.icon}
              </Box>
              <Box sx={{ flex: 1, pt: 0.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827', mb: 0.5 }}>
                  {event.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6B7280', mb: 1 }}>
                  {event.description}
                </Typography>
                <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                  {event.timestamp.toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  // Get applications for selected project
  const getApplicationsForProject = () => {
    if (!selectedProject) return [];
    return applications.filter(app => app.projectId === selectedProject.id);
  };

  // Fetch customer documents when application is selected
  const handleApplicationSelect = async (application: Application) => {
    setSelectedApplication(application);
    setSelectedDocument(null); // Clear document selection when switching applications
    setExpandedDocument(null); // Clear expanded document when switching applications

    // Clear chatbot messages when switching applications
    setMessages([
      {
        id: 1,
        text: "Hello! I'm your AI assistant. How can I help you analyze loan applications?",
        sender: 'bot',
        timestamp: new Date(),
      },
    ]);
    setNewMessage('');
    setChatResponse('');

    setLoadingDocuments(true);

    try {
      // Extract entityReference from the application's documents array
      // The entityReference should be available in the first document's extractedData
      let entityReference = application.id; // fallback to application ID

      if (application.documents && application.documents.length > 0) {
        const firstDoc = application.documents[0];
        if (firstDoc.extractedData && firstDoc.extractedData.entityReference) {
          entityReference = firstDoc.extractedData.entityReference;
        }
      }

      console.log('🔍 Using entityReference:', entityReference);

      // Fetch customer documents
      const documents = await fetchCustomerDocuments(entityReference);
      setCustomerDocuments(documents);

      console.log('📄 Customer documents fetched:', documents);
    } catch (error) {
      console.error('Error fetching customer documents:', error);
      setCustomerDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  // Export customer application data as JSON
  const handleExportApplication = async () => {
    if (!selectedApplication || !selectedProject?.id) return;

    setExportLoading(true);
    try {
      // Extract entityReference from the application's documents
      let entityReference = selectedApplication.id; // fallback to application ID

      if (selectedApplication.documents && selectedApplication.documents.length > 0) {
        const firstDoc = selectedApplication.documents[0];
        if (firstDoc.extractedData && firstDoc.extractedData.entityReference) {
          entityReference = firstDoc.extractedData.entityReference;
        }
      }

      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            contentType: "EKYC_DOCS",
            pageSize: 50,
            pageNumber: 1,
            campaignId: selectedProject.id,
            entityReference: entityReference
          },
          agent: {
            code: "QUERY_CUSTOMER_DOCUMENTS",
            format: "raw"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data && response.data.data) {
        console.log('Export API response:', response.data);
        console.log('Export data to be set:', response.data.data);
        setExportData(response.data.data);
        setExportDialogOpen(true);

        // Download JSON file
        const jsonString = JSON.stringify(response.data.data, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `application_${entityReference}_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setSuccess('Application data exported and downloaded successfully!');
      } else {
        setError('No data available for export');
      }
    } catch (error: any) {
      console.error('Error exporting application data:', error);
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
        return;
      }
      setError('Failed to export application data');
    } finally {
      setExportLoading(false);
    }
  };

  // Fetch customer documents for a specific application
  const fetchCustomerDocuments = async (entityReference: string) => {
    if (!selectedProject?.id) return [];

    try {
      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            contentType: "EKYC_DOCS",
            pageSize: 50,
            pageNumber: 1,
            campaignId: selectedProject.id,
            entityReference: entityReference
          },
          agent: {
            code: "QUERY_CUSTOMER_DOCUMENTS"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data && response.data.data) {
        return response.data.data;
      }
      return [];
    } catch (error: any) {
      console.error('Error fetching customer documents:', error);
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
        return [];
      }
      return [];
    }
  };

  // State for customer documents
  const [customerDocuments, setCustomerDocuments] = useState<any[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  // Export functionality state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportData, setExportData] = useState<any>(null);
  const [exportLoading, setExportLoading] = useState(false);

  // Google Drive integration state
  const [googleDriveAuthState, setGoogleDriveAuthState] = useState<GoogleDriveAuthState>({
    isAuthenticated: false,
    sessionId: ''
  });
  const [googleDriveFiles, setGoogleDriveFiles] = useState<GoogleDriveFile[]>([]);
  const [selectedGoogleFile, setSelectedGoogleFile] = useState<GoogleDriveFile | null>(null);
  const [selectedGoogleFiles, setSelectedGoogleFiles] = useState<GoogleDriveFile[]>([]);
  const [showGoogleDriveDialog, setShowGoogleDriveDialog] = useState(false);
  const [showGoogleDriveAuthDialog, setShowGoogleDriveAuthDialog] = useState(false);
  const [googleDriveAuthUrl, setGoogleDriveAuthUrl] = useState('');
  const [googleDriveAuthWindow, setGoogleDriveAuthWindow] = useState<Window | null>(null);
  const [isGoogleDriveAuthInProgress, setIsGoogleDriveAuthInProgress] = useState(false);
  const [googleDriveSearchQuery, setGoogleDriveSearchQuery] = useState('');
  const [googleDriveFolders, setGoogleDriveFolders] = useState<GoogleDriveFolder[]>([]);
  const [googleDriveActiveTab, setGoogleDriveActiveTab] = useState<'myDrive' | 'shared' | 'teamDrives'>('myDrive');
  const [teamDrives, setTeamDrives] = useState<any[]>([]);
  const [selectedTeamDrive, setSelectedTeamDrive] = useState<string | null>(null);
  const [googleDriveCurrentPage, setGoogleDriveCurrentPage] = useState(1);
  const [isDownloadingGoogleFile, setIsDownloadingGoogleFile] = useState(false);
  const [googleDriveDownloadProgress, setGoogleDriveDownloadProgress] = useState(0);
  const [isLoadingGoogleDriveFiles, setIsLoadingGoogleDriveFiles] = useState(false);
  const [googleDriveError, setGoogleDriveError] = useState<string | null>(null);
  const [googleDriveSuccess, setGoogleDriveSuccess] = useState<string | null>(null);

  // Folder navigation state
  const [currentFolderId, setCurrentFolderId] = useState('root');
  const [folderTree, setFolderTree] = useState<GoogleDriveFolder[]>([]);
  const [showFolderTree, setShowFolderTree] = useState(false);
  const [isLoadingFolderTree, setIsLoadingFolderTree] = useState(false);
  const [folderBreadcrumb, setFolderBreadcrumb] = useState<GoogleDriveBreadcrumb[]>([]);

  // Google Drive service reference
  const googleDriveService = useRef<GoogleDriveService | null>(null);

  // Frontend-only search - filter files based on search query
  const filteredGoogleDriveFiles = googleDriveFiles.filter(file => {
    if (!googleDriveSearchQuery.trim()) return true;

    const query = googleDriveSearchQuery.toLowerCase();
    const fileName = file.name.toLowerCase();
    const fileType = file.mimeType.toLowerCase();

    return fileName.includes(query) ||
      fileType.includes(query) ||
      (file.size && file.size.toLowerCase().includes(query));
  });

  // Helper functions for enhanced Google Drive dialog
  const getFileTypeIcon = (mimeType: string) => {
    if (mimeType?.includes('pdf')) return '📄';
    if (mimeType?.includes('word') || mimeType?.includes('document')) return '📝';
    if (mimeType?.includes('sheet') || mimeType?.includes('excel')) return '📊';
    if (mimeType?.includes('presentation') || mimeType?.includes('powerpoint')) return '📈';
    if (mimeType?.includes('image')) return '🖼️';
    if (mimeType?.includes('folder')) return '📁';
    return '📄';
  };

  const getFileTypeLabel = (mimeType: string) => {
    if (mimeType?.includes('pdf')) return 'PDF';
    if (mimeType?.includes('word') || mimeType?.includes('document')) return 'Word';
    if (mimeType?.includes('sheet') || mimeType?.includes('excel')) return 'Excel';
    if (mimeType?.includes('presentation') || mimeType?.includes('powerpoint')) return 'PowerPoint';
    if (mimeType?.includes('image')) return 'Image';
    if (mimeType?.includes('folder')) return 'Folder';
    return 'File';
  };

  // Image Preview Component
  const ImagePreview = ({ file }: { file: GoogleDriveFile }) => {
    const [imageError, setImageError] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);

    const getImageBackground = (mimeType: string) => {
      return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    };

    if (imageError || !file.thumbnailLink) {
      return (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: getImageBackground(file.mimeType || ''),
            color: 'white',
            borderRadius: '8px',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Typography sx={{ fontSize: '24px', mb: 0.5 }}>
            🖼️
          </Typography>
          <Typography sx={{ fontSize: '10px', textAlign: 'center', px: 1 }}>
            Image
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        {imageLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: getImageBackground(file.mimeType || ''),
              zIndex: 1,
            }}
          >
            <CircularProgress size={20} sx={{ color: 'white' }} />
          </Box>
        )}
        <img
          src={file.thumbnailLink}
          alt={file.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '8px',
          }}
          onLoad={() => setImageLoading(false)}
          onError={() => setImageError(true)}
        />
      </Box>
    );
  };

  // Tab change handler
  const handleGoogleDriveTabChange = (newTab: 'myDrive' | 'shared' | 'teamDrives') => {
    setGoogleDriveActiveTab(newTab);
    setSelectedTeamDrive(null);
  };

  // Team drive selection handler
  const handleTeamDriveSelect = (teamDriveId: string) => {
    setSelectedTeamDrive(teamDriveId);
  };

  // Load files for active tab
  const loadFilesForActiveTab = async () => {
    if (!googleDriveAuthState.isAuthenticated || !googleDriveAuthState.sessionId) return;

    try {
      setIsLoadingGoogleDriveFiles(true);

      if (googleDriveActiveTab === 'myDrive') {
        const result = await googleDriveService.current!.loadMyDriveFiles(googleDriveAuthState.sessionId, 50);
        setGoogleDriveFiles(result.files || []);
      } else if (googleDriveActiveTab === 'shared') {
        const result = await googleDriveService.current!.loadSharedFiles(googleDriveAuthState.sessionId, 50);
        setGoogleDriveFiles(result.files || []);
      } else if (googleDriveActiveTab === 'teamDrives') {
        const drivesResult = await googleDriveService.current!.loadTeamDrives(googleDriveAuthState.sessionId, 50);
        setTeamDrives(drivesResult.drives || []);

        if (selectedTeamDrive) {
          const filesResult = await googleDriveService.current!.loadTeamDriveFiles(googleDriveAuthState.sessionId, selectedTeamDrive, 50);
          setGoogleDriveFiles(filesResult.files || []);
        } else {
          setGoogleDriveFiles([]);
        }
      }
    } catch (error: any) {
      console.error('Error loading files:', error);
      setGoogleDriveError(error.message || 'Failed to load files');
    } finally {
      setIsLoadingGoogleDriveFiles(false);
    }
  };

  // Check Google Drive authentication status on mount and when auth state changes
  useEffect(() => {
    const checkAuthStatus = async () => {
      if (googleDriveAuthState.sessionId && googleDriveService.current) {
        try {
          const isAuthenticated = await googleDriveService.current.checkAuthStatus(googleDriveAuthState.sessionId);
          if (!isAuthenticated && googleDriveAuthState.isAuthenticated) {
            // Session expired, clear auth state
            setGoogleDriveAuthState({
              isAuthenticated: false,
              sessionId: ''
            });
            localStorage.removeItem('googleDriveSessionId');
            setError('Google Drive session expired. Please re-authenticate.');
          }
        } catch (error) {
          console.error('Error checking auth status:', error);
        }
      }
    };

    checkAuthStatus();
  }, [googleDriveAuthState.sessionId, googleDriveAuthState.isAuthenticated]);

  // Load files when dialog opens or active tab/team drive changes
  useEffect(() => {
    if (showGoogleDriveDialog && googleDriveAuthState.isAuthenticated && googleDriveAuthState.sessionId) {
      loadFilesForActiveTab();
    }
  }, [showGoogleDriveDialog, googleDriveActiveTab, googleDriveAuthState.isAuthenticated, googleDriveAuthState.sessionId, selectedTeamDrive]);

  // Dummy data for enhanced UI
  const dummyStatuses = ['completed', 'processing', 'requires_review', 'draft', 'approved', 'pending_verification', 'under_review', 'approved_with_conditions'];
  const dummyLoanTypes = ['Home Loan', 'Personal Loan', 'Business Loan', 'Car Loan', 'Education Loan', 'Gold Loan', 'Property Loan'];
  const dummyBanks = ['HDFC Bank', 'ICICI Bank', 'State Bank of India', 'Axis Bank', 'Kotak Mahindra Bank', 'Punjab National Bank', 'Bank of Baroda'];
  const dummyCities = ['Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Kolkata', 'Hyderabad', 'Pune', 'Ahmedabad', 'Jaipur', 'Lucknow'];
  const dummyStates = ['Maharashtra', 'Delhi', 'Karnataka', 'Tamil Nadu', 'West Bengal', 'Telangana', 'Gujarat', 'Rajasthan', 'Uttar Pradesh'];
  const dummyApplicationTypes = ['New Application', 'Refinance', 'Top-up', 'Balance Transfer', 'Extension'];
  const dummyPriorities = ['High', 'Medium', 'Low', 'Urgent'];
  const dummyAssignedTo = ['John Smith', 'Sarah Johnson', 'Mike Davis', 'Lisa Wang', 'Alex Brown', 'Emma Wilson'];

  // Function to get random status for applications table
  const getRandomStatus = (index: number): Application['status'] => {
    // Use index to ensure consistent status for each application
    const statuses: Application['status'][] = ['completed', 'processing'];
    return statuses[index % statuses.length];
  };

  // Function to get random priority for applications table
  const getRandomPriority = (index: number) => {
    const priorities = ['High', 'Medium', 'Low', 'Urgent'];
    return priorities[index % priorities.length];
  };

  // Function to get random processing time for applications table
  const getRandomProcessingTime = (index: number) => {
    const times = [2, 5, 8, 12, 15, 20, 25, 30];
    return times[index % times.length];
  };

  // Google Drive integration functions
  const initiateGoogleDriveAuth = async () => {
    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return;
    }

    try {
      setIsGoogleDriveAuthInProgress(true);
      setError('');

      const result = await googleDriveService.current.initiateAuth();
      setGoogleDriveAuthUrl(result.authUrl);
      setShowGoogleDriveAuthDialog(true);

      const authWindow = googleDriveService.current.openAuthWindow(result.authUrl);
      if (authWindow) {
        setGoogleDriveAuthWindow(authWindow);
      }

      // Check for authentication completion
      const checkAuthStatus = setInterval(async () => {
        try {
          let isWindowClosed = false;
          let isOnCallbackPage = false;

          try {
            isWindowClosed = authWindow?.closed || false;
          } catch (error) {
            isWindowClosed = false;
          }

          // Check if window is on callback page (indicating successful auth)
          try {
            const currentUrl = authWindow?.location?.href || '';
            isOnCallbackPage = currentUrl.includes('/analytics/google-drive/auth/callback');
          } catch (error) {
            // Ignore cross-origin restrictions
          }

          if (isWindowClosed || isOnCallbackPage) {
            clearInterval(checkAuthStatus);
            setShowGoogleDriveAuthDialog(false);
            setGoogleDriveAuthWindow(null);
            setIsGoogleDriveAuthInProgress(false);

            // Test if authentication was successful using auth status endpoint
            try {
              // Get the sessionId we stored earlier
              const sessionId = localStorage.getItem('googleDriveSessionId');

              if (!sessionId) {
                setError('Authentication failed: No session ID found. Please try again.');
                return;
              }

              // Add a small delay to allow backend to process tokens
              setSuccess('Processing authentication...');
              await new Promise(resolve => setTimeout(resolve, 2000));

              // Try auth status check first
              if (!googleDriveService.current) {
                setError('Google Drive service not initialized');
                return;
              }
              let isAuthenticated = await googleDriveService.current.checkAuthStatus(sessionId);

              if (isAuthenticated) {
                setGoogleDriveAuthState((prev: GoogleDriveAuthState) => ({
                  ...prev,
                  isAuthenticated: true,
                  sessionId: sessionId
                }));

                // Load files and open dialog
                await loadGoogleDriveFilesWithSessionId(sessionId);
                setShowGoogleDriveDialog(true);
              } else {
                // If auth status says not authenticated but we have a sessionId, 
                // try to load files directly to see if authentication actually worked
                try {
                  setSuccess('Verifying authentication by loading files...');
                  // Try to load files directly to see if authentication actually worked
                  try {
                    const result = await googleDriveService.current.loadFiles(sessionId, undefined, undefined, 1, 5);

                    // Authentication actually worked, just the status endpoint is lagging
                    setGoogleDriveAuthState((prev: GoogleDriveAuthState) => ({
                      ...prev,
                      isAuthenticated: true,
                      sessionId: sessionId
                    }));

                    // Load files and open dialog
                    await loadGoogleDriveFilesWithSessionId(sessionId);
                    setShowGoogleDriveDialog(true);
                  } catch (fileError) {
                    // Try one more time with a longer delay
                    setSuccess('Authentication processing, please wait...');
                    await new Promise(resolve => setTimeout(resolve, 3000));

                    isAuthenticated = await googleDriveService.current.checkAuthStatus(sessionId);
                    if (isAuthenticated) {
                      setGoogleDriveAuthState((prev: GoogleDriveAuthState) => ({
                        ...prev,
                        isAuthenticated: true,
                        sessionId: sessionId
                      }));

                      // Load files and open dialog
                      await loadGoogleDriveFilesWithSessionId(sessionId);
                      setShowGoogleDriveDialog(true);
                    } else {
                      setError('Authentication was cancelled or failed. Please try again.');
                    }
                  }
                } catch (fileError) {
                  setError('Authentication was cancelled or failed. Please try again.');
                }
              }
            } catch (error) {
              setError('Authentication was cancelled or failed. Please try again.');
            }
          }
        } catch (error: any) {
          // Auth not complete yet
        }
      }, 2000);

      // Cleanup interval after 5 minutes
      setTimeout(() => {
        clearInterval(checkAuthStatus);
        if (authWindow && !authWindow.closed) {
          authWindow.close();
          setGoogleDriveAuthWindow(null);
        }
        setShowGoogleDriveAuthDialog(false);
        setIsGoogleDriveAuthInProgress(false);
      }, 300000); // 5 minutes timeout
    } catch (error: any) {
      setIsGoogleDriveAuthInProgress(false);

      if (error?.message === 'jwt_expired') {
        navigate('/login');
      } else {
        const errorMessage = error?.response?.data?.error || error?.message || 'Failed to initiate Google Drive authentication';
        setError(`Google Drive authentication failed: ${errorMessage}. Please try again.`);
      }
    }
  };

  const clearGoogleDriveAuth = async () => {
    try {
      // Clear any stored files first
      setGoogleDriveFiles([]);
      setSelectedGoogleFile(null);
      setSelectedGoogleFiles([]);

      // Call backend to revoke tokens if needed
      if (googleDriveService.current && googleDriveAuthState.sessionId) {
        await googleDriveService.current.clearAuth(googleDriveAuthState.sessionId);
      }

      // Clear the authentication state with a new session ID
      const newSessionId = `loan_processing_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      setGoogleDriveAuthState({
        isAuthenticated: false,
        sessionId: newSessionId
      });

      // Clear localStorage
      localStorage.removeItem('googleDriveSessionId');

      setSuccess('Google Drive authentication cleared successfully');
    } catch (error: any) {
      console.error('Error clearing Google Drive auth:', error);
      setError('Failed to clear Google Drive authentication');
    }
  };

  const loadGoogleDriveFilesWithSessionId = async (sessionId: string, searchQuery?: string, folderId?: string) => {
    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return;
    }

    try {
      setIsLoadingGoogleDriveFiles(true);
      setError('');

      await googleDriveService.current.loadFiles(
        sessionId,
        searchQuery,
        folderId || currentFolderId,
        googleDriveCurrentPage,
        1000
      );
    } catch (error: any) {
      console.error('Error loading Google Drive files:', error);
      if (error?.message === 'jwt_expired') {
        navigate('/login');
      } else {
        setError('Failed to load Google Drive files');
      }
    } finally {
      setIsLoadingGoogleDriveFiles(false);
    }
  };

  const loadGoogleDriveFiles = async (searchQuery?: string) => {
    if (!googleDriveAuthState.isAuthenticated || !googleDriveAuthState.sessionId) {
      setError('Please authenticate with Google Drive first.');
      return;
    }

    await loadGoogleDriveFilesWithSessionId(googleDriveAuthState.sessionId, searchQuery);
  };

  const navigateToFolder = async (folderId: string) => {
    if (!googleDriveAuthState.isAuthenticated || !googleDriveAuthState.sessionId) {
      setError('Please authenticate with Google Drive first.');
      return;
    }

    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return;
    }

    try {
      setCurrentFolderId(folderId);
      setGoogleDriveCurrentPage(1);

      await googleDriveService.current.loadFiles(
        googleDriveAuthState.sessionId,
        googleDriveSearchQuery,
        folderId,
        1,
        20
      );

      await getFolderBreadcrumb(folderId);
    } catch (error: any) {
      console.error('Error navigating to folder:', error);
      setError('Failed to navigate to folder');
    }
  };

  const navigateToBreadcrumb = async (folderId: string) => {
    await navigateToFolder(folderId);
  };

  const getFolderBreadcrumb = async (folderId: string) => {
    if (!googleDriveAuthState.isAuthenticated || !googleDriveAuthState.sessionId) {
      return;
    }

    if (!googleDriveService.current) {
      return;
    }

    try {
      await googleDriveService.current.getFolderBreadcrumb(
        googleDriveAuthState.sessionId,
        folderId
      );
    } catch (error: any) {
      console.error('Error getting folder breadcrumb:', error);
    }
  };

  const loadFolderTree = async () => {
    if (!googleDriveAuthState.isAuthenticated || !googleDriveAuthState.sessionId) {
      setError('Please authenticate with Google Drive first.');
      return;
    }

    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return;
    }

    try {
      setIsLoadingFolderTree(true);
      await googleDriveService.current.loadFolderTree(
        googleDriveAuthState.sessionId,
        'root',
        3
      );
    } catch (error: any) {
      console.error('Error loading folder tree:', error);
      if (error?.message === 'jwt_expired') {
        navigate('/login');
      }
      setError('Failed to load folder tree');
    } finally {
      setIsLoadingFolderTree(false);
    }
  };

  const searchGoogleDriveFiles = async () => {
    // Frontend-only search - no API calls needed
    // Files are filtered in real-time as user types
  };

  const downloadGoogleDriveFile = async (file: GoogleDriveFile) => {
    if (!googleDriveAuthState.isAuthenticated || !googleDriveAuthState.sessionId) {
      setError('Please authenticate with Google Drive first.');
      return;
    }

    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return;
    }

    setIsDownloadingGoogleFile(true);
    setGoogleDriveDownloadProgress(0);
    setSelectedGoogleFile(file);

    try {
      // Get download URL and download file
      const downloadUrl = await googleDriveService.current.getDownloadUrl(
        googleDriveAuthState.sessionId,
        file.id
      );

      const downloadedFile = await googleDriveService.current.downloadFile(
        downloadUrl,
        file.name,
        (progress) => setGoogleDriveDownloadProgress(progress)
      );

      // Process the downloaded file using existing upload flow
      if (downloadedFile) {
        // Create a FormData object for the file
        const formData = new FormData();
        formData.append('file', downloadedFile);
        formData.append('token', `Bearer ${localStorage.getItem('token')}`);
        formData.append('type', 'docs');

        try {
          // Upload file
          const uploadResponse = await axios.post(
            `${environment.apiUrl}/account/uploadFileForOrg`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              }
            }
          );

          if (uploadResponse.data.status === 'success') {
            console.log('📤 Google Drive file uploaded successfully:', uploadResponse.data.path);

            // Extract entityReference from the application's documents
            let entityReference = null;

            if (selectedApplication && selectedApplication.documents && selectedApplication.documents.length > 0) {
              const firstDoc = selectedApplication.documents[0];
              if (firstDoc.extractedData && firstDoc.extractedData.entityReference) {
                entityReference = firstDoc.extractedData.entityReference;
              }
            }

            if (!entityReference) {
              console.error('❌ No entityReference available for document indexing');
              setError('Failed to get entity reference for document indexing');
              return;
            }

            console.log('🔍 Using entityReference for indexing:', entityReference);

            // Index the document with form data
            const indexPayload = {
              data: {
                filePath: uploadResponse.data.path,
                sourceName: "Google Drive",
                fileType: "docs",
                contentType: "EKYC_DOCS",
                campaignId: selectedProject?.id || '',
                entityReference: entityReference
              },
              agent: {
                code: "INDEX_CUSTOMER_DOCUMENT"
              }
            };

            console.log('🔍 Indexing Google Drive document with payload:', indexPayload);

            const indexResponse = await axios.post(
              `${environment.controlplaneUrl}/agent/flow/execute`,
              indexPayload,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`,
                  'Content-Type': 'application/json',
                }
              }
            );

            console.log('✅ Google Drive document indexed successfully:', indexResponse.data);

            setSuccess('Document uploaded successfully, AI system is processing your document. Refresh the data in a few minutes to view the extracted data.');

            // Close Google Drive dialog
            setShowGoogleDriveDialog(false);

            // Refresh applications
            fetchApplications();
          }
        } catch (uploadError: any) {
          console.error('Error uploading Google Drive file:', uploadError);
          if (uploadError?.response?.data?.error === 'jwt malformed' || uploadError?.response?.data?.error === 'jwt expired') {
            navigate('/login');
          } else {
            setError('Failed to upload Google Drive document');
          }
        }
      }
    } catch (error: any) {
      console.error('Error downloading Google Drive file:', error);
      if (error?.message === 'jwt_expired') {
        navigate('/login');
      } else {
        setError('Failed to download Google Drive file');
      }
    } finally {
      setIsDownloadingGoogleFile(false);
      setGoogleDriveDownloadProgress(0);
    }
  };

  const getFileInfo = async (file: GoogleDriveFile) => {
    if (!googleDriveAuthState.isAuthenticated || !googleDriveAuthState.sessionId) {
      setError('Please authenticate with Google Drive first.');
      return;
    }

    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return;
    }

    try {
      await googleDriveService.current.getFileInfo(
        googleDriveAuthState.sessionId,
        file.id
      );
    } catch (error: any) {
      console.error('Error getting file info:', error);
      if (error?.message === 'jwt_expired') {
        navigate('/login');
      }
      setError('Failed to get file information');
    }
  };

  const downloadMultipleGoogleDriveFiles = async (files: GoogleDriveFile[]) => {
    if (!googleDriveAuthState.isAuthenticated || !googleDriveAuthState.sessionId) {
      setError('Please authenticate with Google Drive first.');
      return;
    }

    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return;
    }

    try {
      setIsDownloadingGoogleFile(true);
      setGoogleDriveDownloadProgress(0);
      setSelectedGoogleFiles(files);

      let totalProgress = 0;
      const totalFiles = files.length;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        try {
          // Get download URL and download file
          const downloadUrl = await googleDriveService.current.getDownloadUrl(
            googleDriveAuthState.sessionId,
            file.id
          );

          const downloadedFile = await googleDriveService.current.downloadFile(
            downloadUrl,
            file.name,
            (progress) => {
              const fileProgress = (progress / totalFiles);
              totalProgress = ((i / totalFiles) * 100) + fileProgress;
              setGoogleDriveDownloadProgress(totalProgress);
            }
          );

          // Process the downloaded file
          if (downloadedFile) {
            // Create a FormData object for the file
            const formData = new FormData();
            formData.append('file', downloadedFile);
            formData.append('token', `Bearer ${localStorage.getItem('token')}`);
            formData.append('type', 'docs');

            try {
              // Upload file
              const uploadResponse = await axios.post(
                `${environment.apiUrl}/account/uploadFileForOrg`,
                formData,
                {
                  headers: {
                    'Content-Type': 'multipart/form-data',
                  }
                }
              );

              if (uploadResponse.data.status === 'success') {
                console.log(`📤 Google Drive file ${i + 1}/${totalFiles} uploaded successfully:`, uploadResponse.data.path);

                // Extract entityReference from the application's documents
                let entityReference = null;

                if (selectedApplication && selectedApplication.documents && selectedApplication.documents.length > 0) {
                  const firstDoc = selectedApplication.documents[0];
                  if (firstDoc.extractedData && firstDoc.extractedData.entityReference) {
                    entityReference = firstDoc.extractedData.entityReference;
                  }
                }

                if (!entityReference) {
                  console.error('❌ No entityReference available for document indexing');
                  setError('Failed to get entity reference for document indexing');
                  return;
                }

                // Index the document
                const indexPayload = {
                  data: {
                    filePath: uploadResponse.data.path,
                    sourceName: "Google Drive",
                    fileType: "docs",
                    contentType: "EKYC_DOCS",
                    campaignId: selectedProject?.id || '',
                    entityReference: entityReference
                  },
                  agent: {
                    code: "INDEX_CUSTOMER_DOCUMENT"
                  }
                };

                const indexResponse = await axios.post(
                  `${environment.controlplaneUrl}/agent/flow/execute`,
                  indexPayload,
                  {
                    headers: {
                      Authorization: `Bearer ${localStorage.getItem('token')}`,
                      'Content-Type': 'application/json',
                    }
                  }
                );

                console.log(`✅ Google Drive document ${i + 1}/${totalFiles} indexed successfully:`, indexResponse.data);
              }
            } catch (uploadError: any) {
              console.error(`Error uploading Google Drive file ${i + 1}/${totalFiles}:`, uploadError);
              if (uploadError?.response?.data?.error === 'jwt malformed' || uploadError?.response?.data?.error === 'jwt expired') {
                navigate('/login');
                return;
              }
            }
          }
        } catch (error: any) {
          console.error(`Error downloading Google Drive file ${i + 1}/${totalFiles}:`, error);
          if (error?.message === 'jwt_expired') {
            navigate('/login');
            return;
          }
        }
      }

      setSuccess(`Successfully processed ${totalFiles} Google Drive documents`);

      // Close Google Drive dialog
      setShowGoogleDriveDialog(false);

      // Refresh applications
      fetchApplications();
    } catch (error: any) {
      console.error('Error processing multiple Google Drive files:', error);
      if (error?.message === 'jwt_expired') {
        navigate('/login');
      } else {
        setError('Failed to process multiple Google Drive files');
      }
    } finally {
      setIsDownloadingGoogleFile(false);
      setGoogleDriveDownloadProgress(0);
    }
  };

  const handleGoogleDriveFileSelect = (file: GoogleDriveFile, event: React.MouseEvent) => {
    event.stopPropagation();

    if (selectedGoogleFile?.id === file.id) {
      setSelectedGoogleFile(null);
    } else {
      setSelectedGoogleFile(file);
    }
  };

  const openGoogleDriveDialog = () => {
    if (!googleDriveAuthState.isAuthenticated) {
      initiateGoogleDriveAuth();
    } else {
      setShowGoogleDriveDialog(true);
      loadGoogleDriveFiles();
    }
  };

  const closeGoogleDriveDialog = () => {
    setShowGoogleDriveDialog(false);
    setSelectedGoogleFile(null);
    setSelectedGoogleFiles([]);
    setGoogleDriveSearchQuery('');
    setGoogleDriveCurrentPage(1);
    setCurrentFolderId('root');
    setFolderBreadcrumb([]);
    setShowFolderTree(false);
  };

  // Check authentication status
  const checkGoogleDriveAuthStatus = async (sessionId: string) => {
    if (!googleDriveService.current) {
      return false;
    }

    try {
      return await googleDriveService.current.checkAuthStatus(sessionId);
    } catch (error: any) {
      return false;
    }
  };

  // Get file preview information
  const getFilePreviewInfo = async (fileId: string) => {
    if (!googleDriveAuthState.isAuthenticated || !googleDriveAuthState.sessionId) {
      setError('Please authenticate with Google Drive first.');
      return;
    }

    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return;
    }

    try {
      await googleDriveService.current.getFilePreviewInfo(
        googleDriveAuthState.sessionId,
        fileId
      );
    } catch (error: any) {
      console.error('Error getting file preview info:', error);
      if (error?.message === 'jwt_expired') {
        navigate('/login');
      }
      setError('Failed to get file preview information');
    }
  };

  // Function to generate random dummy data for applications
  const generateDummyData = (index: number, actualCity?: string, actualState?: string) => {
    const randomStatus = dummyStatuses[Math.floor(Math.random() * dummyStatuses.length)];
    const randomLoanType = dummyLoanTypes[Math.floor(Math.random() * dummyLoanTypes.length)];
    const randomBank = dummyBanks[Math.floor(Math.random() * dummyBanks.length)];
    const randomApplicationType = dummyApplicationTypes[Math.floor(Math.random() * dummyApplicationTypes.length)];
    const randomPriority = dummyPriorities[Math.floor(Math.random() * dummyPriorities.length)];
    const randomAssignedTo = dummyAssignedTo[Math.floor(Math.random() * dummyAssignedTo.length)];

    return {
      status: randomStatus,
      loanType: randomLoanType,
      bank: randomBank,
      city: actualCity || dummyCities[Math.floor(Math.random() * dummyCities.length)],
      state: actualState || dummyStates[Math.floor(Math.random() * dummyStates.length)],
      riskScore: Math.floor(Math.random() * 100) + 1,
      processingTime: Math.floor(Math.random() * 30) + 1,
      approvalRate: Math.floor(Math.random() * 40) + 60, // 60-100%
      lastUpdated: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
      applicationType: randomApplicationType,
      priority: randomPriority,
      assignedTo: randomAssignedTo,
      estimatedCompletion: new Date(Date.now() + Math.random() * 14 * 24 * 60 * 60 * 1000).toISOString()
    };
  };

  // Get input fields for querying documents
  const fetchInputFields = async () => {
    try {
      const response = await axios.post(
        `${environment.apiUrl}/agent/getPayload`,
        {
          data: {},
          agent: {
            code: "QUERY_RELEVANT_DOCUMENTS"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data && response.data.data) {
        return response.data.data;
      }
      return [];
    } catch (error: any) {
      console.error('Error fetching input fields:', error);
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
        return [];
      }
      return [];
    }
  };

  // Google Drive utility functions
  const formatFileSize = (bytes: string) => {
    if (!bytes || bytes === '0') return '0 B';
    const size = parseInt(bytes);
    if (size === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Unknown';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
    if (mimeType.includes('image')) return '🖼️';
    if (mimeType.includes('video')) return '🎥';
    if (mimeType.includes('audio')) return '🎵';
    if (mimeType.includes('text')) return '📄';
    return '📁';
  };

  // Get content types for document generation
  const fetchContentTypes = async () => {
    try {
      const response = await axios.post(
        `${environment.apiUrl}/account/getContentTypes`,
        {
          data: {}
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data) {
        // Filter only content types with class "Semi Formal" as per business logic
        return response.data.filter((contentType: any) => contentType.class === "Semi Formal");
      }
      return [];
    } catch (error: any) {
      console.error('Error fetching content types:', error);
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
        return [];
      }
      return [];
    }
  };

  // Generate content from document
  const generateContentFromDocument = async (docUID: string, contentType: string) => {
    try {
      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            docUID: docUID,
            contentType: contentType
          },
          agent: {
            code: "GENERATE_CONTENT_FROM_NEWS_ITEM"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data && response.data.data && response.data.data.length > 0) {
        const firstItem = response.data.data[0];
        if (firstItem.entries && firstItem.entries.length > 0) {
          const generatedContentEntry = firstItem.entries.find((entry: any) => entry.key === 'generatedContent');
          return generatedContentEntry ? generatedContentEntry.value : null;
        }
      }
      return null;
    } catch (error: any) {
      console.error('Error generating content:', error);
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
        return null;
      }
      return null;
    }
  };

  // Query regulatory document differences
  const queryDocumentDifferences = async (docUID: string, refDocUID: string) => {
    try {
      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            docUID: docUID,
            refDocUID: refDocUID
          },
          agent: {
            code: "QUERY_REGULATORY_DOCUMENT_DIFFERENCES"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data && response.data.data && response.data.data.length > 0) {
        return response.data.data[0];
      }
      return null;
    } catch (error: any) {
      console.error('Error querying document differences:', error);
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
        return null;
      }
      return null;
    }
  };



  return (
    <Box sx={{ padding: '10px' }}>
      {/* Header - matching DocsInsights.tsx */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px !important',
        height: '80px !important',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIosIcon sx={{ fontSize: 18 }} />}
            onClick={() => {
              // Check if we have a stored navigation context
              const context = sessionStorage.getItem('appsNavigationContext');
              if (context && context.startsWith('industry:')) {
                // Navigate back to Apps with the stored context
                navigate('/Apps');
              } else {
                // Default navigation to Apps
                navigate('/Apps');
              }
            }}
            sx={{
              color: '#9C27B0',
              fontWeight: 600,
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: '#F3E5F5'
              }
            }}
          >
            Back
          </Button>
          <AccountBalanceIcon sx={{ fontSize: 24, color: '#9C27B0', marginLeft: '24px' }} />
          <Typography variant="h5" sx={{ color: '#131F32', fontWeight: 600, fontSize: '1.25rem' }}>
            Loan Processing
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl sx={{ minWidth: 220 }} size="small">
            <InputLabel>Select Project</InputLabel>
            <Select
              value={selectedProject?.id || ''}
              onChange={e => {
                const projectId = e.target.value as string;
                const proj = projects.find(p => p.id === projectId);
                if (proj) {
                  setSelectedProject(proj);
                }
              }}
              label="Select Project"
              disabled={isLoadingProjects}
            >
              {isLoadingProjects ? (
                <MenuItem disabled>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} sx={{ color: '#9C27B0' }} />
                    Loading projects...
                  </Box>
                </MenuItem>
              ) : (
                projects.map(project => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsProjectDialogOpen(true)}
            sx={{
              backgroundColor: '#9C27B0',
              '&:hover': { backgroundColor: '#7B1FA2' },
              fontWeight: 600
            }}
          >
            Create Project
          </Button>
        </Box>
      </Box>

      {/* Access Denied Check */}
      {!canViewProject && selectedProject ? (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          p: 4
        }}>
          <Box sx={{
            textAlign: 'center',
            maxWidth: 500,
            p: 4,
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
          }}>
            <LockIcon sx={{ fontSize: 64, color: '#f44336', mb: 2 }} />
            <Typography variant="h4" sx={{ mb: 2, color: '#333' }}>
              Access Denied
            </Typography>
            <Typography variant="h6" sx={{ mb: 3, color: '#666' }}>
              You don't have access to this project
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: '#666' }}>
              Please create a new project or select another project that you have access to.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setIsProjectDialogOpen(true)}
                sx={{
                  backgroundColor: '#9C27B0',
                  '&:hover': {
                    backgroundColor: '#7B1FA2',
                  }
                }}
              >
                Create New Project
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => window.location.reload()}
                sx={{
                  borderColor: '#9C27B0',
                  color: '#9C27B0',
                  '&:hover': {
                    borderColor: '#7B1FA2',
                    backgroundColor: 'rgba(156, 39, 176, 0.1)',
                  }
                }}
              >
                Refresh
              </Button>
            </Box>
          </Box>
        </Box>
      ) : (
        <>
          {/* Top-Level Tabs - matching DocsInsights.tsx */}
          <Box sx={{
            borderBottom: 1,
            borderColor: 'divider',

          }}>

          </Box>

          {/* Main Content - matching DocsInsights.tsx layout */}
          {isFullScreen ? (
            <FullScreenContainer>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 3,
                pb: 2,
                borderBottom: '1px solid #E0E0E0',
                flexShrink: 0
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    border: '2px solid #fff'
                  }}>
                    <img src={botIcon} alt="bot" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ color: '#131F32', fontWeight: 600, fontSize: '0.875rem' }}>
                      AI Assistant
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: '#64748B', fontSize: '0.75rem' }}>
                      {selectedDocument ?
                        `Document: ${getShortTitle(selectedDocument.entries?.find((e: any) => e.key === 'documentTitle')?.value) || 'Selected Document'}` :
                        selectedApplication?.name || 'No application selected'
                      }
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  onClick={() => setIsFullScreen(false)}
                  sx={{
                    color: '#9C27B0',
                    '&:hover': {
                      backgroundColor: '#F3E5F5'
                    }
                  }}
                >
                  <FullscreenExitIcon />
                </IconButton>
              </Box>
              <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                overflow: 'hidden',
                height: 'calc(100vh - 100px)'
              }}>
                <Box sx={{
                  flex: 1,
                  overflowY: 'auto',
                  backgroundColor: '#FAFBFC',
                  borderRadius: '12px',
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  minHeight: 0,
                  mb: 2
                }}>
                  {messages.map((message) => (
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: message.sender === 'user' ? 'flex-end' : 'flex-start',
                      mb: 2
                    }}>
                      <MessageBubble sender={message.sender}>
                        <Box sx={{ position: 'relative' }}>
                          {formatMessageContent(message)}
                          <Typography variant="caption" sx={{
                            display: 'block',
                            mt: 1,
                            color: '#64748B',
                            fontSize: '0.7rem',
                            textAlign: message.sender === 'user' ? 'right' : 'left',
                            opacity: 0.8
                          }}>
                            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Box>
                      </MessageBubble>
                    </Box>
                  ))}
                  {isTyping && (
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 2,
                      borderRadius: '12px',
                      backgroundColor: 'rgba(156, 39, 176, 0.1)',
                      width: 'fit-content',
                      marginLeft: '16px',
                      marginBottom: '8px'
                    }}>
                      <Box sx={{
                        display: 'flex',
                        gap: 0.5,
                        ml: 0.5
                      }}>
                        {[0, 1, 2].map((i) => (
                          <Box key={i} sx={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#9C27B0',
                            animation: `pulse 1.4s ease-in-out infinite ${i * 0.2}s`,
                            '@keyframes pulse': {
                              '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                              '40%': { opacity: 1, transform: 'scale(1)' }
                            }
                          }} />
                        ))}
                      </Box>
                    </Box>
                  )}
                  <div ref={messagesEndRef} />
                </Box>

                {/* Input for fullscreen */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  borderTop: '1px solid #E0E0E0',
                  pt: 2
                }}>
                  <TextField
                    fullWidth
                    placeholder="Ask about loan applications..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    variant="outlined"
                    size="small"
                    multiline
                    maxRows={3}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '20px',
                        backgroundColor: '#FAFBFC',
                      }
                    }}
                  />
                  <IconButton
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim()}
                    sx={{
                      backgroundColor: '#9C27B0',
                      color: 'white',
                      '&:hover': { backgroundColor: '#7B1FA2' },
                      '&:disabled': { backgroundColor: '#E0E0E0' }
                    }}
                  >
                    <SendIcon />
                  </IconButton>
                </Box>
              </Box>
            </FullScreenContainer>
          ) : (
            // Main two-panel layout - matching DocsInsights.tsx
            <Box sx={{ display: 'flex', gap: 3 }}>
              {/* Left Panel - Applications/Content */}
              <Box sx={{ flex: (topLevelTab === 1 && !showApplicationDetail) || !chatOpen ? 1 : 2 }}>
                <Paper sx={{
                  p: 3,
                  borderRadius: '16px',
                  minHeight: 'calc(100vh - 180px)'
                }}>
                  {/* Content based on selected tab */}
                  {topLevelTab === 0 && (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#131F32' }}>
                          Overview Dashboard
                        </Typography>
                        {/* <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setNewApplicationDialogOpen(true)}
                      sx={{
                        backgroundColor: '#9C27B0',
                        '&:hover': {
                          backgroundColor: '#7B1FA2'
                        }
                      }}
                    >
                      New Application
                    </Button> */}
                      </Box>

                      {/* Summary Cards Row */}
                      <Grid container spacing={3} mb={3} >
                        <Grid item xs={12} sm={6} md={3}>
                          <Card sx={{
                            p: 3,
                            borderRadius: '12px',
                            background: '#ffffff',
                            color: '#1f2937',
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: '#d1d5db',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                            }
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box sx={{
                                p: 1.5,
                                borderRadius: 2,
                                backgroundColor: '#f3f4f6',
                                color: '#6366f1'
                              }}>
                                <AssignmentIcon sx={{ fontSize: 24 }} />
                              </Box>
                              <Box>
                                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#1f2937' }}>
                                  {loading ? (
                                    <CircularProgress size={24} sx={{ color: '#9C27B0' }} />
                                  ) : (
                                    applications.length
                                  )}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                  Total Applications
                                </Typography>
                              </Box>
                            </Box>
                          </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Card sx={{
                            p: 3,
                            borderRadius: '12px',
                            background: '#ffffff',
                            color: '#1f2937',
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: '#d1d5db',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                            }
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box sx={{
                                p: 1.5,
                                borderRadius: 2,
                                backgroundColor: '#f0fdf4',
                                color: '#16a34a'
                              }}>
                                <CheckCircleIcon sx={{ fontSize: 24 }} />
                              </Box>
                              <Box>
                                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#1f2937' }}>
                                  {loading ? (
                                    <CircularProgress size={24} sx={{ color: '#9C27B0' }} />
                                  ) : (
                                    applications.filter(app => (app.dummyData?.status || app.status) === 'completed' || (app.dummyData?.status || app.status) === 'approved').length
                                  )}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                  Approved
                                </Typography>
                              </Box>
                            </Box>
                          </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Card sx={{
                            p: 3,
                            borderRadius: '12px',
                            background: '#ffffff',
                            color: '#1f2937',
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: '#d1d5db',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                            }
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box sx={{
                                p: 1.5,
                                borderRadius: 2,
                                backgroundColor: '#fef3c7',
                                color: '#d97706'
                              }}>
                                <PendingActionsIcon sx={{ fontSize: 24 }} />
                              </Box>
                              <Box>
                                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#1f2937' }}>
                                  {loading ? (
                                    <CircularProgress size={24} sx={{ color: '#9C27B0' }} />
                                  ) : (
                                    applications.filter(app => (app.dummyData?.status || app.status) === 'processing' || (app.dummyData?.status || app.status) === 'pending_verification').length
                                  )}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                  Processing
                                </Typography>
                              </Box>
                            </Box>
                          </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Card sx={{
                            p: 3,
                            borderRadius: '12px',
                            background: '#ffffff',
                            color: '#1f2937',
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: '#d1d5db',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                            }
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box sx={{
                                p: 1.5,
                                borderRadius: 2,
                                backgroundColor: '#fdf4ff',
                                color: '#c026d3'
                              }}>
                                <TrendingUpIcon sx={{ fontSize: 24 }} />
                              </Box>
                              <Box>
                                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#1f2937' }}>
                                  {applications.length > 0 ? Math.floor(applications.reduce((sum, app) => sum + (app.dummyData?.riskScore || 0), 0) / applications.length) : 0}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                  Avg Risk Score
                                </Typography>
                              </Box>
                            </Box>
                          </Card>
                        </Grid>
                      </Grid>

                      {/* Additional Metrics Row */}
                      <Grid container spacing={3} mb={3}>
                        <Grid item xs={12} sm={6} md={3}>
                          <Card sx={{
                            p: 3,
                            borderRadius: '12px',
                            background: '#ffffff',
                            color: '#1f2937',
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: '#d1d5db',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                            }
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box sx={{
                                p: 1.5,
                                borderRadius: 2,
                                backgroundColor: '#e0f2fe',
                                color: '#0288d1'
                              }}>
                                <AccessTimeIcon sx={{ fontSize: 24 }} />
                              </Box>
                              <Box>
                                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#1f2937' }}>
                                  {applications.length > 0 ? Math.floor(applications.reduce((sum, app) => sum + (app.dummyData?.processingTime || 0), 0) / applications.length) : 0}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                  Avg Processing Days
                                </Typography>
                              </Box>
                            </Box>
                          </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Card sx={{
                            p: 3,
                            borderRadius: '12px',
                            background: '#ffffff',
                            color: '#1f2937',
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: '#d1d5db',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                            }
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box sx={{
                                p: 1.5,
                                borderRadius: 2,
                                backgroundColor: '#f0fdf4',
                                color: '#16a34a'
                              }}>
                                <CheckCircleIcon sx={{ fontSize: 24 }} />
                              </Box>
                              <Box>
                                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#1f2937' }}>
                                  {applications.length > 0 ? Math.floor(applications.reduce((sum, app) => sum + (app.dummyData?.approvalRate || 0), 0) / applications.length) : 0}%
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                  Avg Approval Rate
                                </Typography>
                              </Box>
                            </Box>
                          </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Card sx={{
                            p: 3,
                            borderRadius: '12px',
                            background: '#ffffff',
                            color: '#1f2937',
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: '#d1d5db',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                            }
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box sx={{
                                p: 1.5,
                                borderRadius: 2,
                                backgroundColor: '#fff3e0',
                                color: '#f57c00'
                              }}>
                                <PersonIcon sx={{ fontSize: 24 }} />
                              </Box>
                              <Box>
                                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#1f2937' }}>
                                  {applications.filter(app => app.dummyData?.priority === 'High' || app.dummyData?.priority === 'Urgent').length}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                  High Priority Apps
                                </Typography>
                              </Box>
                            </Box>
                          </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Card sx={{
                            p: 3,
                            borderRadius: '12px',
                            background: '#ffffff',
                            color: '#1f2937',
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: '#d1d5db',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                            }
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box sx={{
                                p: 1.5,
                                borderRadius: 2,
                                backgroundColor: '#fce4ec',
                                color: '#c2185b'
                              }}>
                                <SecurityIcon sx={{ fontSize: 24 }} />
                              </Box>
                              <Box>
                                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#1f2937' }}>
                                  {applications.filter(app => (app.dummyData?.riskScore || 0) > 70).length}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                  High Risk Apps
                                </Typography>
                              </Box>
                            </Box>
                          </Card>
                        </Grid>
                      </Grid>

                      {/* Charts Row */}
                      <Grid container spacing={3}>
                        {/* First Column */}
                        <Grid item xs={12} lg={6}>

                          {/* First Row - Applications by Status Pie Chart */}
                          <Card sx={{
                            p: 3,
                            borderRadius: '16px',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                            mb: 3
                          }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#131F32' }}>
                              Applications by Status
                            </Typography>
                            {(() => {
                              // Count applications by status (including dummy data)
                              const statusCounts = applications.reduce((acc: { [key: string]: number }, app) => {
                                const status = app.dummyData?.status || app.status;
                                acc[status] = (acc[status] || 0) + 1;
                                return acc;
                              }, {});

                              if (Object.keys(statusCounts).length === 0) {
                                return (
                                  <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                                      No status data available
                                    </Typography>
                                  </Box>
                                );
                              }

                              const statusColors: { [key: string]: string } = {
                                'completed': '#10B981',
                                'approved': '#059669',
                                'processing': '#F59E0B',
                                'requires_review': '#EF4444',
                                'draft': '#6B7280',
                                'pending_verification': '#F97316',
                                'under_review': '#8B5CF6',
                                'approved_with_conditions': '#06B6D4'
                              };

                              // Convert to pie chart data format
                              const pieData = Object.entries(statusCounts).map(([status, count]) => ({
                                name: status.replace('_', ' ').toUpperCase(),
                                value: count,
                                color: statusColors[status] || '#6B7280'
                              }));

                              return (
                                <Box sx={{ height: 300, width: '100%' }}>
                                  <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                      <Pie
                                        data={pieData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                        outerRadius={80}
                                        fill="#8884d8"
                                        dataKey="value"
                                      >
                                        {pieData.map((entry, index) => (
                                          <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                      </Pie>
                                      <Legend verticalAlign="bottom" height={36} />
                                    </PieChart>
                                  </ResponsiveContainer>
                                </Box>
                              );
                            })()}
                          </Card>



                          {/* Second Row - Applications by Type */}
                          <Card sx={{
                            p: 3,
                            borderRadius: '16px',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
                          }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#131F32' }}>
                              Applications by Type
                            </Typography>
                            {(() => {
                              // Count applications by loan type using dummy data
                              const loanTypeCounts = applications.reduce((acc: { [key: string]: number }, app) => {
                                const loanType = app.dummyData?.loanType || 'Unknown';
                                acc[loanType] = (acc[loanType] || 0) + 1;
                                return acc;
                              }, {});

                              if (Object.keys(loanTypeCounts).length === 0) {
                                return (
                                  <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                                      No type data available
                                    </Typography>
                                  </Box>
                                );
                              }

                              const colors = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#43e97b', '#38f9d7'];
                              const maxCount = Math.max(...Object.values(loanTypeCounts));

                              return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  {Object.entries(loanTypeCounts).map(([loanType, count], idx) => {
                                    const pct = (count / maxCount) * 100;
                                    return (
                                      <Box key={loanType} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Typography variant="body2" sx={{ width: 100, color: '#131F32', fontWeight: 500, fontSize: '0.75rem' }}>
                                          {loanType}
                                        </Typography>
                                        <Box sx={{
                                          flex: 1,
                                          height: 12,
                                          backgroundColor: '#F3F4F6',
                                          borderRadius: 6,
                                          overflow: 'hidden'
                                        }}>
                                          <Box sx={{
                                            width: `${pct}%`,
                                            height: '100%',
                                            backgroundColor: colors[idx % colors.length],
                                            borderRadius: 6,
                                            transition: 'width 0.3s ease',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                          }} />
                                        </Box>
                                        <Typography variant="body2" sx={{
                                          width: 40,
                                          textAlign: 'right',
                                          color: '#64748B',
                                          fontWeight: 600,
                                          fontSize: '0.75rem'
                                        }}>
                                          {count}
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              );
                            })()}
                          </Card>
                        </Grid>

                        {/* Second Column - Recent Applications (spans both rows) */}
                        <Grid item xs={12} lg={6}>
                          <Card sx={{
                            p: 3,
                            borderRadius: '16px',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                            height: '100%'
                          }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#131F32' }}>
                              Recent Applications
                            </Typography>
                            {(() => {
                              const recentApplications = applications.slice(0, 4);

                              if (recentApplications.length === 0) {
                                return (
                                  <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                                      No applications available
                                    </Typography>
                                  </Box>
                                );
                              }

                              return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  {recentApplications.map((app, index) => (
                                    <Box key={app.id} sx={{
                                      p: 2,
                                      borderRadius: '8px',
                                      backgroundColor: '#F8FAFC',
                                      border: '1px solid #E2E8F0',
                                      cursor: 'pointer',
                                      '&:hover': {
                                        backgroundColor: '#F1F5F9',
                                        borderColor: '#9C27B0',
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 4px 12px rgba(156, 39, 176, 0.1)'
                                      },
                                      transition: 'all 0.2s ease'
                                    }}
                                      onClick={() => {
                                        handleApplicationSelect(app);
                                        setTopLevelTab(1);
                                        setShowApplicationDetail(true);
                                      }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                        <Avatar sx={{
                                          backgroundColor: '#F3E5F5',
                                          color: '#9C27B0',
                                          width: 32,
                                          height: 32
                                        }}>
                                          <PersonIcon sx={{ fontSize: 16 }} />
                                        </Avatar>
                                        <Box sx={{ flex: 1 }}>
                                          <Typography variant="body2" sx={{
                                            fontWeight: 600,
                                            color: '#131F32',
                                            mb: 0.5
                                          }}>
                                            {app.name}
                                          </Typography>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {app.status !== 'unknown' && getStatusIcon(app.status)}
                                            {app.status !== 'unknown' && (
                                              <Chip
                                                label={app.status.replace('_', ' ')}
                                                size="small"
                                                sx={{
                                                  backgroundColor: getStatusColor(app.status),
                                                  color: 'white',
                                                  fontSize: '0.625rem',
                                                  height: '16px',

                                                  textTransform: 'capitalize'
                                                }}
                                              />
                                            )}
                                          </Box>
                                        </Box>
                                      </Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" sx={{
                                          color: '#64748B',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 0.5
                                        }}>
                                          <DescriptionIcon sx={{ fontSize: 12 }} />
                                          {app.documentsCount} documents
                                        </Typography>
                                        <Typography variant="caption" sx={{
                                          color: '#64748B',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 0.5
                                        }}>
                                          <AccessTimeIcon sx={{ fontSize: 12 }} />
                                          {new Date(app.createdAt).toLocaleDateString()}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  ))}
                                </Box>
                              );
                            })()}
                          </Card>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                  {topLevelTab === 1 && (
                    <Box>
                      {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#131F32' }}>
                      Loan Applications
                        </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setNewApplicationDialogOpen(true)}
                      sx={{
                        backgroundColor: '#9C27B0',
                        '&:hover': { backgroundColor: '#7B1FA2' }
                      }}
                    >
                      New Application
                    </Button>
                      </Box> */}

                      {!showApplicationDetail && (
                        <Box>
                          {/* Search and Filter Controls */}
                          <Box sx={{
                            backgroundColor: '#ffffff',
                            // borderRadius: '12px', 
                            // boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', 
                            p: 3,
                            mb: 3
                          }}>
                            <Box sx={{
                              display: 'flex',
                              flexDirection: { xs: 'column', lg: 'row' },
                              alignItems: { xs: 'stretch', lg: 'center' },
                              justifyContent: 'space-between',
                              gap: 2
                            }}>
                              {/* Search */}
                              <Box sx={{ flex: 1, maxWidth: { xs: '100%', lg: '400px' } }}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  placeholder="Search applications..."
                                  value={searchTerm}
                                  onChange={(e) => setSearchTerm(e.target.value)}
                                  InputProps={{
                                    startAdornment: (
                                      <SearchIcon sx={{ mr: 1, color: '#9CA3AF' }} />
                                    ),
                                  }}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: '8px',
                                      '&:hover fieldset': { borderColor: '#9C27B0' },
                                      '&.Mui-focused fieldset': { borderColor: '#9C27B0' }
                                    }
                                  }}
                                />
                              </Box>

                              {/* Controls */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {/* Help Text - Left of Refresh Button */}
                                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                                  <Typography variant="caption" sx={{
                                    color: '#9CA3AF',
                                    fontSize: '0.75rem !important',
                                    display: 'block',
                                    lineHeight: 1.2
                                  }}>
                                    Click refresh to see latest documents
                                  </Typography>
                                  {lastFetchedTime && (
                                    <Typography variant="caption" sx={{
                                      color: '#9CA3AF',
                                      fontSize: '0.65rem !important',
                                      display: 'block',
                                      fontStyle: 'italic',
                                      lineHeight: 1.2
                                    }}>
                                      Last refreshed: {new Date(lastFetchedTime).toLocaleString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        hour12: true
                                      })}
                                    </Typography>
                                  )}
                                </Box>

                                {/* Refresh Button */}
                                <Tooltip title={lastFetchedTime ? `Last refreshed: ${lastFetchedTime.toLocaleString()}` : 'Refresh applications'}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={
                                      <RefreshIcon sx={{
                                        fontSize: 18,
                                        animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                                        '@keyframes spin': {
                                          '0%': { transform: 'rotate(0deg)' },
                                          '100%': { transform: 'rotate(360deg)' }
                                        }
                                      }} />
                                    }
                                    onClick={async () => {
                                      setIsRefreshing(true);
                                      await fetchApplications();
                                    }}
                                    disabled={isRefreshing || loading}
                                    sx={{
                                      color: '#9C27B0',
                                      borderColor: '#E5E9EF',
                                      borderRadius: '8px',
                                      height: '40px',
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      textTransform: 'none',
                                      px: 1.5,
                                      backgroundColor: 'white',
                                      '&:hover': {
                                        backgroundColor: '#F3E5F5',
                                        borderColor: '#9C27B0'
                                      },
                                      '&.Mui-disabled': {
                                        color: '#D1D5DB',
                                        borderColor: '#E5E9EF'
                                      }
                                    }}
                                  >
                                    {isRefreshing ? 'Refreshing...' : 'Refresh'}
                                  </Button>
                                </Tooltip>

                                {/* Status Filter */}
                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                  <Select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value as 'all' | 'completed' | 'processing')}
                                    displayEmpty
                                    size="small"
                                    sx={{
                                      borderRadius: '8px',
                                      '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: statusFilter !== 'all' ? '#3B82F6' : '#D1D5DB'
                                      }
                                    }}
                                  >
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="completed">Completed</MenuItem>
                                    <MenuItem value="processing">In Progress</MenuItem>
                                  </Select>
                                </FormControl>

                                {/* Create New Application Button - Disabled if user lacks edit permission */}
                                {getApplicationsForProject().length > 0 && (
                                  <Tooltip title={!canEditProject ? "Edit permission required to create applications" : ""}>
                                    <span>
                                      <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={() => setNewApplicationDialogOpen(true)}
                                        disabled={!canEditProject}
                                        sx={{
                                          boredr: '1px solid #9C27B0',
                                          backgroundColor: canEditProject ? 'white' : '#f5f5f5',
                                          color: canEditProject ? '#9C27B0' : '#9e9e9e',
                                          fontWeight: '700 !important',
                                          '&:hover': {
                                            backgroundColor: canEditProject ? 'white' : '#f5f5f5'
                                          },
                                          '&.Mui-disabled': {
                                            backgroundColor: '#f5f5f5',
                                            color: '#9e9e9e',
                                            border: '1px solid #e0e0e0'
                                          },
                                          borderRadius: '8px',
                                          // px: 3,
                                          p: 1,
                                          // py: 1.5
                                        }}
                                      >
                                        Create New Application
                                      </Button>
                                    </span>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                          </Box>

                          {/* Applications Table */}
                          <Box sx={{
                            backgroundColor: '#ffffff',
                            borderRadius: '16px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                            overflow: 'hidden',
                            border: '1px solid #F1F5F9'
                          }}>
                            {loading ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                                <CircularProgress size={48} sx={{ color: '#9C27B0' }} />
                              </Box>
                            ) : (() => {
                              // Filter applications based on search and status
                              const applications = getApplicationsForProject();
                              const filteredApplications = applications.filter(app => {
                                const matchesSearch = searchTerm === '' ||
                                  app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                  app.id.toLowerCase().includes(searchTerm.toLowerCase());

                                let matchesStatus = statusFilter === 'all';
                                if (!matchesStatus) {
                                  const randomStatus = getRandomStatus(applications.indexOf(app));
                                  matchesStatus = randomStatus === statusFilter;
                                }

                                return matchesSearch && matchesStatus;
                              });

                              if (filteredApplications.length === 0) {
                                return (
                                  <Box sx={{ textAlign: 'center', py: 8 }}>
                                    {getApplicationsForProject().length === 0 ? (
                                      <>
                                        <DescriptionIcon sx={{ fontSize: 64, color: '#E5E9EF', mb: 2 }} />
                                        <Typography variant="h6" sx={{ color: '#64748B', mb: 1 }}>
                                          No applications yet
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#94A3B8', mb: 3 }}>
                                          Create your first loan application to get started
                                        </Typography>
                                        <Tooltip title={!canEditProject ? "Edit permission required to create applications" : ""}>
                                          <span>
                                            <Button
                                              variant="contained"
                                              onClick={() => setNewApplicationDialogOpen(true)}
                                              disabled={!canEditProject}
                                              sx={{
                                                backgroundColor: canEditProject ? '#9C27B0' : '#f5f5f5',
                                                color: canEditProject ? 'white' : '#9e9e9e',
                                                '&:hover': {
                                                  backgroundColor: canEditProject ? '#7B1FA2' : '#f5f5f5'
                                                },
                                                '&.Mui-disabled': {
                                                  backgroundColor: '#f5f5f5',
                                                  color: '#9e9e9e'
                                                }
                                              }}
                                            >
                                              Create New Application
                                            </Button>
                                          </span>
                                        </Tooltip>
                                      </>
                                    ) : (
                                      <>
                                        <SearchIcon sx={{ fontSize: 64, color: '#E5E9EF', mb: 2 }} />
                                        <Typography variant="h6" sx={{ color: '#64748B', mb: 1 }}>
                                          No applications found
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#94A3B8', mb: 3 }}>
                                          Try adjusting your search terms or filters to find what you're looking for.
                                        </Typography>
                                        <Button
                                          variant="text"
                                          onClick={() => {
                                            setSearchTerm('');
                                            setStatusFilter('all');
                                          }}
                                          sx={{ color: '#2563EB' }}
                                        >
                                          Clear all filters
                                        </Button>
                                      </>
                                    )}
                                  </Box>
                                );
                              }

                              // Pagination
                              const itemsPerPage = 5;
                              const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);
                              const startIndex = (currentPage - 1) * itemsPerPage;
                              const paginatedApplications = filteredApplications.slice(startIndex, startIndex + itemsPerPage);

                              // Helper function to mask ID
                              const maskId = (id: string) => {
                                if (id.length <= 8) return id;
                                return id.substring(0, 4) + '****' + id.substring(id.length - 4);
                              };

                              return (
                                <>
                                  <Box sx={{ overflowX: 'auto' }}>
                                    <TableContainer>
                                      <Table sx={{ minWidth: 800 }}>
                                        <TableHead>
                                          <TableRow sx={{ backgroundColor: '#FAFBFC' }}>
                                            <TableCell sx={{
                                              fontWeight: 700,
                                              color: '#475569',
                                              fontSize: '0.875rem !important',
                                              py: 3,
                                              px: 3,
                                              letterSpacing: '0.025em !important',
                                              wordSpacing: '0.1em !important',
                                              lineHeight: '1.4 !important',
                                              width: '45%',
                                              minWidth: '300px'
                                            }}>
                                              Application
                                            </TableCell>
                                            <TableCell sx={{
                                              fontWeight: 700,
                                              color: '#475569',
                                              fontSize: '0.875rem !important',
                                              py: 3,
                                              px: 3,
                                              letterSpacing: '0.025em !important',
                                              wordSpacing: '0.1em !important',
                                              lineHeight: '1.4 !important',
                                              width: '20%',
                                              minWidth: '120px'
                                            }}>
                                              Status
                                            </TableCell>
                                            <TableCell sx={{
                                              fontWeight: 700,
                                              color: '#475569',
                                              fontSize: '0.875rem !important',
                                              py: 3,
                                              px: 3,
                                              letterSpacing: '0.025em !important',
                                              wordSpacing: '0.1em !important',
                                              lineHeight: '1.4 !important',
                                              width: '20%',
                                              minWidth: '120px'
                                            }}>
                                              Created At
                                            </TableCell>
                                            <TableCell sx={{
                                              fontWeight: 700,
                                              color: '#475569',
                                              fontSize: '0.875rem !important',
                                              py: 3,
                                              px: 3,
                                              letterSpacing: '0.025em !important',
                                              wordSpacing: '0.1em !important',
                                              lineHeight: '1.4 !important',
                                              width: '15%',
                                              minWidth: '120px'
                                            }}>
                                              Action
                                            </TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {paginatedApplications.map((application, index) => (
                                            <TableRow
                                              key={application.id}
                                              sx={{
                                                '&:hover': { backgroundColor: '#F8FAFC' },
                                                transition: 'all 0.2s ease',
                                                borderBottom: '1px solid #F1F5F9',
                                                '&:last-child': { borderBottom: 'none' }
                                              }}
                                            >
                                              <TableCell sx={{ py: 3, px: 3, verticalAlign: 'top' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                  <Box sx={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: '12px',
                                                    backgroundColor: '#F3E5F5',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    mr: 3,
                                                    border: '1px solid #E9D5FD',
                                                    flexShrink: 0
                                                  }}>
                                                    <DescriptionIcon sx={{
                                                      color: '#9C27B0',
                                                      fontSize: 24,
                                                      fontWeight: 600
                                                    }} />
                                                  </Box>
                                                  <Box sx={{ minWidth: 0, flex: 1 }}>
                                                    <Typography variant="body1" sx={{
                                                      fontWeight: 600,
                                                      color: '#1E293B',
                                                      fontSize: '0.95rem !important',
                                                      letterSpacing: '0.025em !important',
                                                      wordSpacing: '0.05em !important',
                                                      lineHeight: '1.5 !important',
                                                      mb: 0.5,
                                                      overflow: 'hidden',
                                                      textOverflow: 'ellipsis',
                                                      whiteSpace: 'nowrap'
                                                    }}>
                                                      {application.name}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{
                                                      color: '#64748B',
                                                      fontSize: '0.75rem !important',
                                                      letterSpacing: '0.025em !important',
                                                      wordSpacing: '0.1em !important',
                                                      lineHeight: '1.4 !important',
                                                      display: 'block',
                                                      overflow: 'hidden',
                                                      textOverflow: 'ellipsis',
                                                      whiteSpace: 'nowrap'
                                                    }}>
                                                      {application.city && application.state ? `${application.city}, ${application.state}` : ''}
                                                    </Typography>
                                                  </Box>
                                                </Box>
                                              </TableCell>
                                              <TableCell sx={{ py: 3, px: 3, verticalAlign: 'middle' }}>
                                                {(() => {
                                                  const randomStatus = getRandomStatus(applications.indexOf(application));
                                                  return (
                                                    <Chip
                                                      label={"Completed"}
                                                      size="small"
                                                      sx={{
                                                        backgroundColor: 'white',
                                                        color: getStatusColor("completed"),
                                                        fontSize: '0.75rem !important',
                                                        height: '24px',
                                                        fontWeight: 600,
                                                        letterSpacing: '0.025em !important',
                                                        wordSpacing: '0.05em !important',
                                                        lineHeight: '1.4 !important',
                                                        borderRadius: '6px !important',
                                                        px: 1.5,
                                                        border: `1px solid ${getStatusColor("completed")}`,
                                                        '& .MuiChip-label': {
                                                          px: 1.5
                                                        }
                                                      }}
                                                    />
                                                  );
                                                })()}
                                              </TableCell>

                                              <TableCell sx={{ py: 3, px: 3, verticalAlign: 'middle' }}>
                                                <Typography variant="body2" sx={{
                                                  color: '#475569',
                                                  fontWeight: 500,
                                                  fontSize: '0.875rem !important',
                                                  letterSpacing: '0.025em !important',
                                                  wordSpacing: '0.05em !important',
                                                  lineHeight: '1.4 !important',
                                                  whiteSpace: 'nowrap'
                                                }}>
                                                  {new Date(application.createdAt).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric'
                                                  })}
                                                </Typography>
                                              </TableCell>
                                              <TableCell sx={{ py: 3, px: 3, verticalAlign: 'middle' }}>
                                                <Button
                                                  size="small"
                                                  variant="text"
                                                  startIcon={<VisibilityIcon />}
                                                  onClick={() => {
                                                    handleApplicationSelect(application);
                                                    setShowApplicationDetail(true);
                                                  }}
                                                  sx={{
                                                    color: '#9C27B0',
                                                    fontSize: '0.75rem !important',
                                                    fontWeight: 600,
                                                    minWidth: '120px',
                                                    px: 1.5,
                                                    py: 0.5,
                                                    whiteSpace: 'nowrap',
                                                    '&:hover': {
                                                      backgroundColor: '#F3E5F5'
                                                    }
                                                  }}
                                                >
                                                  View Details
                                                </Button>
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  </Box>

                                  {/* Pagination */}
                                  {totalPages > 1 && (
                                    <Box sx={{
                                      backgroundColor: '#ffffff',
                                      px: 3,
                                      py: 2,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      borderTop: '1px solid #E5E7EB'
                                    }}>
                                      <Box>
                                        <Typography variant="body2" sx={{ color: '#6B7280' }}>
                                          Showing <Box component="span" sx={{ fontWeight: 600 }}>{startIndex + 1}</Box> to{' '}
                                          <Box component="span" sx={{ fontWeight: 600 }}>
                                            {Math.min(startIndex + itemsPerPage, filteredApplications.length)}
                                          </Box>{' '}
                                          of <Box component="span" sx={{ fontWeight: 600 }}>{filteredApplications.length}</Box> applications
                                        </Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <IconButton
                                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                          disabled={currentPage === 1}
                                          sx={{
                                            color: currentPage === 1 ? '#D1D5DB' : '#6B7280',
                                            '&:hover': { backgroundColor: currentPage === 1 ? 'transparent' : '#F3F4F6' }
                                          }}
                                        >
                                          <ChevronLeftIcon />
                                        </IconButton>

                                        {/* Page Numbers */}
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                          <Button
                                            key={page}
                                            onClick={() => setCurrentPage(page)}
                                            variant={page === currentPage ? "contained" : "text"}
                                            size="small"
                                            sx={{
                                              minWidth: 'auto',
                                              px: 1,
                                              py: 0.5,
                                              backgroundColor: page === currentPage ? '#3B82F6' : 'transparent',
                                              color: page === currentPage ? 'white' : '#6B7280',
                                              '&:hover': {
                                                backgroundColor: page === currentPage ? '#2563EB' : '#F3F4F6'
                                              }
                                            }}
                                          >
                                            {page}
                                          </Button>
                                        ))}

                                        <IconButton
                                          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                          disabled={currentPage === totalPages}
                                          sx={{
                                            color: currentPage === totalPages ? '#D1D5DB' : '#6B7280',
                                            '&:hover': { backgroundColor: currentPage === totalPages ? 'transparent' : '#F3F4F6' }
                                          }}
                                        >
                                          <ChevronRightIcon />
                                        </IconButton>
                                      </Box>
                                    </Box>
                                  )}
                                </>
                              );
                            })()}
                          </Box>
                        </Box>
                      )}

                      {showApplicationDetail && (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Button
                              variant="text"
                              startIcon={<KeyboardBackspaceIcon />}
                              onClick={() => {
                                setShowApplicationDetail(false);
                                setSelectedApplication(null); // Clear selected application when going back
                                setSelectedDocument(null); // Clear document selection
                                setExpandedDocument(null); // Clear expanded document
                                // Clear chatbot messages when going back to table view
                                setMessages([
                                  {
                                    id: 1,
                                    text: "Hello! I'm your AI assistant. How can I help you analyze loan applications?",
                                    sender: 'bot',
                                    timestamp: new Date(),
                                  },
                                ]);
                                setNewMessage('');
                                setChatResponse('');
                              }}
                              sx={{ color: '#9C27B0' }}
                            >
                              Applications
                            </Button>
                          </Box>

                          {selectedApplication ? (
                            <Box>
                              {/* Application Header Card */}
                              <Box sx={{ border: 'none', mb: 5, mt: 5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                  <Avatar sx={{ backgroundColor: '#F3E5F5', color: '#9C27B0', width: 48, height: 48 }}>
                                    <PersonIcon sx={{ fontSize: 24 }} />
                                  </Avatar>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#131F32', mb: 0.5 }}>
                                      {selectedApplication.name}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                      {selectedApplication.status !== 'unknown' && getStatusIcon(selectedApplication.status)}
                                      {selectedApplication.status !== 'unknown' && (
                                        <Chip
                                          label={selectedApplication.status.replace('_', ' ')}
                                          size="small"
                                          sx={{
                                            backgroundColor: getStatusColor(selectedApplication.status),
                                            color: 'white',
                                            fontWeight: 600,
                                            textTransform: 'capitalize'
                                          }}
                                        />
                                      )}
                                      <Typography variant="body2" sx={{ color: '#64748B' }}>
                                        Created {new Date(selectedApplication.createdAt).toLocaleDateString()}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button
                                      variant="outlined"
                                      startIcon={<DownloadIcon />}
                                      onClick={handleExportApplication}
                                      disabled={exportLoading}
                                      sx={{
                                        borderColor: '#9C27B0',
                                        color: '#9C27B0',
                                        '&:hover': {
                                          backgroundColor: '#F3F4F6',
                                          borderColor: '#4B5563'
                                        },

                                      }}
                                    >
                                      {exportLoading ? 'Exporting...' : 'Export'}
                                    </Button>
                                    <Tooltip title={!canEditProject ? "Edit permission required to add documents" : ""}>
                                      <span>
                                        <Button
                                          variant="outlined"
                                          startIcon={<UploadIcon />}
                                          onClick={() => setUploadDialogOpen(true)}
                                          disabled={!canEditProject}
                                          sx={{
                                            borderColor: canEditProject ? '#9C27B0' : '#e0e0e0',
                                            color: canEditProject ? '#9C27B0' : '#9e9e9e',
                                            '&:hover': {
                                              backgroundColor: canEditProject ? '#F3E5F5' : '#f5f5f5',
                                              borderColor: canEditProject ? '#7B1FA2' : '#e0e0e0'
                                            },
                                            '&.Mui-disabled': {
                                              backgroundColor: '#f5f5f5',
                                              color: '#9e9e9e',
                                              borderColor: '#e0e0e0'
                                            }
                                          }}
                                        >
                                          Add Documents
                                        </Button>
                                      </span>
                                    </Tooltip>
                                  </Box>
                                </Box>
                              </Box>

                              {/* Application Details Tabs */}
                              <Card sx={{ overflow: 'hidden' }}>
                                <Box sx={{ borderBottom: '1px solid #E5E7EB' }}>
                                  <Box sx={{ display: 'flex', px: 3 }}>
                                    {[
                                      { id: 'summary', name: 'Summary', icon: <PersonIcon sx={{ fontSize: 18 }} /> },
                                      { id: 'documents', name: 'Documents', icon: <DescriptionIcon sx={{ fontSize: 18 }} /> },
                                      { id: 'timeline', name: 'Timeline', icon: <AccessTimeIcon sx={{ fontSize: 18 }} /> }
                                    ].map((tab) => (
                                      <Button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id as 'summary' | 'documents' | 'timeline')}
                                        variant="text"
                                        sx={{
                                          color: activeTab === tab.id ? '#9C27B0' : '#6B7280',
                                          borderBottom: activeTab === tab.id ? '2px solid #9C27B0' : '2px solid transparent',
                                          borderRadius: 0,
                                          py: 2,
                                          px: 3,
                                          '&:hover': {
                                            backgroundColor: 'transparent',
                                            color: activeTab === tab.id ? '#2563EB' : '#374151'
                                          }
                                        }}
                                      >
                                        {tab.icon}
                                        <Typography sx={{ ml: 1 }}>{tab.name}</Typography>
                                      </Button>
                                    ))}
                                  </Box>
                                </Box>

                                {/* Tab Content */}
                                <Box sx={{ p: 3 }}>
                                  {activeTab === 'summary' && renderSummaryTab()}
                                  {activeTab === 'documents' && renderDocumentsTab()}
                                  {activeTab === 'timeline' && renderTimelineTab()}
                                </Box>
                              </Card>
                            </Box>
                          ) : (
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                              <AssignmentIcon sx={{ fontSize: 64, color: '#E5E9EF', mb: 2 }} />
                              <Typography variant="h6" sx={{ color: '#64748B', mb: 1 }}>
                                No Application Selected
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#94A3B8', mb: 3 }}>
                                Select an application from the Applications tab to view detailed analysis
                              </Typography>
                              <Button
                                variant="contained"
                                onClick={() => setShowApplicationDetail(false)}
                                sx={{
                                  backgroundColor: '#9C27B0',
                                  '&:hover': { backgroundColor: '#7B1FA2' }
                                }}
                              >
                                View Applications
                              </Button>
                            </Box>
                          )}
                        </Box>

                      )}
                    </Box>)}
                </Paper>
              </Box>

              {/* Right Panel - Chat Interface */}
              <Box sx={{
                flex: 1.3,
                display: (topLevelTab === 1 && !showApplicationDetail) || !chatOpen ? 'none' : 'block'
              }}>
                <Paper sx={{
                  p: 3,
                  borderRadius: '16px',
                  // height: 'calc(100vh - 180px)',
                  height: '90vh',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  {/* Chat Header */}
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 3,
                    pb: 2,
                    borderBottom: '1px solid #E0E0E0',
                    justifyContent: 'space-between'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        border: '2px solid #fff'
                      }}>
                        <img src={botIcon} alt="bot" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Chat Assistant</Typography>
                        <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.65rem !important' }}>
                          {selectedDocument ?
                            `Document: ${getShortTitle(selectedDocument.entries?.find((e: any) => e.key === 'documentTitle')?.value) || 'Selected Document'}` :
                            selectedApplication?.name || 'All Applications'
                          }
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton
                        onClick={() => setIsFullScreen(!isFullScreen)}
                        sx={{
                          color: '#9C27B0',
                          '&:hover': {
                            backgroundColor: '#F3E5F5'
                          }
                        }}
                      >
                        {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          setChatOpen(false);
                          setSelectedDocument(null);
                        }}
                        sx={{
                          color: '#6b7280',
                          '&:hover': {
                            backgroundColor: '#f3f4f6'
                          }
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Messages */}
                  <Box sx={{
                    flex: 1,
                    overflowY: 'auto',
                    mb: 1,
                    p: 2,
                    backgroundColor: '#FAFBFC',
                    borderRadius: '12px'
                  }}>
                    {messages.map((message) => (
                      <Box key={message.id} sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: message.sender === 'user' ? 'flex-end' : 'flex-start',
                        mb: 2
                      }}>
                        <MessageBubble sender={message.sender}>
                          <Box sx={{ position: 'relative' }}>
                            {formatMessageContent(message)}
                            <Typography variant="caption" sx={{
                              display: 'block',
                              mt: 1,
                              color: '#64748B',
                              fontSize: '0.7rem',
                              textAlign: message.sender === 'user' ? 'right' : 'left',
                              opacity: 0.8
                            }}>
                              {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </Box>
                        </MessageBubble>
                      </Box>
                    ))}
                    {isTyping && (
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 2,
                        borderRadius: '12px',
                        backgroundColor: 'rgba(156, 39, 176, 0.1)',
                        width: 'fit-content',
                        marginLeft: '16px',
                        marginBottom: '8px'
                      }}>
                        <Box sx={{
                          display: 'flex',
                          gap: 0.5,
                          ml: 0.5
                        }}>
                          {[0, 1, 2].map((i) => (
                            <Box key={i} sx={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: '#9C27B0',
                              animation: `pulse 1.4s ease-in-out infinite ${i * 0.2}s`,
                              '@keyframes pulse': {
                                '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                                '40%': { opacity: 1, transform: 'scale(1)' }
                              }
                            }} />
                          ))}
                        </Box>
                      </Box>
                    )}
                    <div ref={messagesEndRef} />
                  </Box>

                  {/* Suggested Questions */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.25,
                      mb: 0.5
                    }}>
                      {suggestedQuestions.map((question, index) => (
                        <Chip
                          key={index}
                          label={question.text}
                          icon={question.icon}
                          onClick={() => {
                            setNewMessage(question.text);
                            handleSendMessage();
                          }}
                          sx={{
                            backgroundColor: '#F3E5F5',
                            color: '#9C27B0',
                            fontSize: '0.625rem',
                            fontWeight: 400,
                            height: '20px',
                            padding: '0 6px',
                            '& .MuiChip-icon': {
                              fontSize: '12px',
                              marginLeft: '4px',
                            },
                            '&:hover': {
                              backgroundColor: '#E1BEE7',
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>

                  {/* Input */}
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <TextField
                      fullWidth
                      placeholder="Ask about loan applications..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      variant="outlined"
                      size="small"
                      multiline
                      maxRows={3}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '20px',
                          backgroundColor: '#FAFBFC',
                        }
                      }}
                    />
                    <IconButton
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim()}
                      sx={{
                        backgroundColor: '#9C27B0',
                        color: 'white',
                        '&:hover': { backgroundColor: '#7B1FA2' },
                        '&:disabled': { backgroundColor: '#E0E0E0' }
                      }}
                    >
                      <SendIcon />
                    </IconButton>
                  </Box>
                </Paper>
              </Box>
            </Box>
          )}

          {/* Success/Error Snackbars */}
          <Snackbar
            open={!!success}
            autoHideDuration={6000}
            onClose={() => setSuccess('')}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
              {success}
            </Alert>
          </Snackbar>

          <Snackbar
            open={!!error}
            autoHideDuration={6000}
            onClose={() => setError('')}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          </Snackbar>

          {/* Upload Document Dialog - exact match from SurveyInsights.tsx */}
          <Dialog
            open={uploadDialogOpen}
            onClose={() => {
              setUploadDialogOpen(false);
              setUploadMethod('device');
              setFileUrl('');
              setUrlValidationError('');
              setIsUrlValidating(false);
              setIsDownloading(false);
              setDownloadProgress(0);
            }}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Upload New Document</Typography>
                <IconButton onClick={() => {
                  setUploadDialogOpen(false);
                  setUploadMethod('device');
                  setFileUrl('');
                  setUrlValidationError('');
                  setIsUrlValidating(false);
                  setIsDownloading(false);
                  setDownloadProgress(0);
                }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                {/* Upload Method Selection */}
                <Box sx={{ mb: 3 }}>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4} p={1}>
                      <UploadMethodCard
                        className={uploadMethod === 'device' ? 'selected' : ''}
                        onClick={() => setUploadMethod('device')}
                      >
                        <CardContent sx={{ textAlign: 'center', p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <UploadMethodIcon>
                            <UploadIcon sx={{ fontSize: 24, color: '#9C27B0' }} />
                          </UploadMethodIcon>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1a1a1a' }}>
                            Upload from Device
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            Select a file from your computer or device
                          </Typography>
                        </CardContent>
                      </UploadMethodCard>
                    </Grid>
                    <Grid item xs={12} md={4} p={1}>
                      <UploadMethodCard
                        className={uploadMethod === 'url' ? 'selected' : ''}
                        onClick={() => setUploadMethod('url')}
                      >
                        <CardContent sx={{ textAlign: 'center', p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <UploadMethodIcon>
                            <OpenInNewIcon sx={{ fontSize: 24, color: '#9C27B0' }} />
                          </UploadMethodIcon>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1a1a1a' }}>
                            Provide File Link
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            Download from Public URL (supports SharePoint, Google Drive, etc.)
                          </Typography>
                        </CardContent>
                      </UploadMethodCard>
                    </Grid>
                    <Grid item xs={12} md={4} p={1}>
                      <UploadMethodCard
                        className={uploadMethod === 'google-drive' ? 'selected' : ''}
                        onClick={() => setUploadMethod('google-drive')}
                      >
                        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 3 }}>
                          <UploadMethodIcon>
                            <AssessmentIcon sx={{ fontSize: 24, color: '#9C27B0' }} />
                          </UploadMethodIcon>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1a1a1a' }}>
                            Google Drive
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            Select a file from your Google Drive
                          </Typography>
                        </CardContent>
                      </UploadMethodCard>
                    </Grid>
                  </Grid>
                </Box>

                {/* Upload Method Specific Content */}
                {uploadMethod === 'device' ? (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1a1a1a' }}>
                      Upload File from Device
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                      Please select a file from your device. Supported: PDF, DOC, DOCX, TXT, Images (JPG, PNG, GIF, BMP, TIFF, WebP)
                    </Typography>
                    <input
                      accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.tif,.webp,.heic,.heif"
                      style={{ display: 'none' }}
                      id="document-upload"
                      type="file"
                      onChange={handleFileUpload}
                    />
                    <label htmlFor="document-upload">
                      <Button
                        variant="contained"
                        component="span"
                        startIcon={<UploadIcon />}
                        disabled={uploadLoading}
                        sx={{
                          backgroundColor: '#9C27B0',
                          '&:hover': {
                            backgroundColor: '#7B1FA2',
                          },
                          '&.Mui-disabled': {
                            backgroundColor: 'rgba(156, 39, 176, 0.3)',
                            color: '#ffffff'
                          }
                        }}
                      >
                        {uploadLoading ? 'Uploading...' : 'Select File'}
                      </Button>
                    </label>
                  </Box>
                ) : uploadMethod === 'url' ? (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1a1a1a' }}>
                      Download from File URL
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                      Provide a direct link to your file. We support public URLs and authenticated URLs (SharePoint, Google Drive, etc.).
                    </Typography>

                    {/* SharePoint-specific guidance */}
                    {fileUrl.includes('sharepoint.com') && (
                      <Alert
                        severity="info"
                        sx={{
                          mb: 2,
                          '& .MuiAlert-icon': {
                            color: '#9C27B0'
                          }
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                          SharePoint File Detected
                        </Typography>
                        <Typography variant="body2">
                          This appears to be a SharePoint file. The system will open SharePoint in a new tab for secure authentication.
                        </Typography>
                        <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                          <li>SharePoint will open in a new tab for login</li>
                          <li>Download the file from SharePoint to your computer</li>
                          <li>Then upload the downloaded file using the device upload option</li>
                          <li>This approach avoids iframe restrictions and ensures security</li>
                        </ul>
                      </Alert>
                    )}

                    {/* Google Drive/Sheets guidance */}
                    {(fileUrl.includes('drive.google.com') || fileUrl.includes('docs.google.com') || fileUrl.includes('sheets.google.com')) && (
                      <Alert
                        severity="info"
                        sx={{
                          mb: 2,
                          '& .MuiAlert-icon': {
                            color: '#9C27B0'
                          }
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                          Google Drive/Sheets File Detected
                        </Typography>
                        <Typography variant="body2">
                          This appears to be a Google Drive or Google Sheets file. The system will attempt automatic download for public files.
                        </Typography>
                        <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                          <li>For public files: Automatic download and processing</li>
                          <li>For private files: Manual authentication in new tab</li>
                          <li>Supports Google Sheets, Docs, and Drive files</li>
                          <li>Secure handling with proper authentication fallback</li>
                        </ul>
                      </Alert>
                    )}

                    <TextField
                      fullWidth
                      label="File URL"
                      placeholder="https://example.com/document.pdf"
                      value={fileUrl}
                      onChange={(e) => setFileUrl(e.target.value)}
                      disabled={uploadLoading || isUrlValidating || isDownloading}
                      sx={{ mb: 2 }}
                      error={!!urlValidationError}
                      helperText={urlValidationError}
                    />

                    <Button
                      variant="contained"
                      startIcon={<OpenInNewIcon />}
                      onClick={handleUrlUpload}
                      disabled={!fileUrl.trim() || uploadLoading || isUrlValidating || isDownloading}
                      sx={{
                        backgroundColor: '#9C27B0',
                        '&:hover': {
                          backgroundColor: '#7B1FA2',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: 'rgba(156, 39, 176, 0.3)',
                          color: '#ffffff'
                        }
                      }}
                    >
                      {isUrlValidating ? 'Validating URL...' :
                        isDownloading ? 'Downloading & Processing...' :
                          uploadLoading ? 'Uploading...' : 'Download & Process'}
                    </Button>

                    {/* Progress indicators */}
                    {(isUrlValidating || isDownloading || uploadLoading) && (
                      <Box sx={{ mt: 2 }}>
                        <LinearProgress
                          variant={isDownloading || uploadLoading ? "determinate" : "indeterminate"}
                          value={isDownloading || uploadLoading ? downloadProgress : undefined}
                          sx={{
                            color: '#9C27B0',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: '#9C27B0'
                            }
                          }}
                        />
                        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                          {isUrlValidating ? 'Validating file URL and checking access...' :
                            isDownloading ? `Downloading and processing file... ${Math.round(downloadProgress)}%` :
                              uploadLoading ? 'Processing and uploading file...' : ''}
                        </Typography>
                        {isDownloading && downloadProgress > 0 && downloadProgress < 100 && (
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontStyle: 'italic' }}>
                            {fileUrl.includes('githubusercontent.com') ? 'Downloading from GitHub...' :
                              fileUrl.includes('sharepoint.com') ? 'If authentication is required, you\'ll be prompted to log in' :
                                'Processing file on server...'}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                ) : uploadMethod === 'google-drive' ? (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                      Authenticate with Google Drive and select a file to upload.
                    </Typography>

                    {googleDriveAuthState.isAuthenticated && (
                      <Box sx={{
                        mb: 2,
                        p: 2,
                        backgroundColor: 'rgba(16, 185, 129, 0.05)',
                        borderRadius: 2,
                        border: '1px solid rgba(16, 185, 129, 0.1)'
                      }}>
                        <Typography variant="body2" sx={{ color: '#10B981', fontWeight: 600, mb: 1 }}>
                          ✅ Connected to Google Drive
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={clearGoogleDriveAuth}
                          sx={{
                            borderColor: '#10B981',
                            color: '#10B981',
                            '&:hover': {
                              borderColor: '#059669',
                              backgroundColor: 'rgba(16, 185, 129, 0.05)'
                            }
                          }}
                        >
                          Switch Account
                        </Button>
                      </Box>
                    )}

                    <Button
                      variant="contained"
                      startIcon={<AssessmentIcon />}
                      onClick={openGoogleDriveDialog}
                      sx={{
                        backgroundColor: '#9C27B0',
                        '&:hover': {
                          backgroundColor: '#7B1FA2',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: 'rgba(156, 39, 176, 0.3)',
                          color: '#ffffff'
                        }
                      }}
                    >
                      {googleDriveAuthState.isAuthenticated ? 'Browse & Select File' : 'Connect to Google Drive'}
                    </Button>
                  </Box>
                ) : null}

                {/* Status Messages */}
                {success && (
                  <Alert
                    severity="success"
                    sx={{
                      mt: 2,
                      '& .MuiAlert-icon': {
                        color: '#9C27B0'
                      }
                    }}
                  >
                    {success}
                  </Alert>
                )}
                {error && !googleDriveAuthState.isAuthenticated && (
                  <Alert
                    severity="error"
                    sx={{
                      mt: 2,
                      '& .MuiAlert-icon': {
                        color: '#9C27B0'
                      }
                    }}
                  >
                    {error}
                  </Alert>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button
                onClick={() => {
                  setUploadDialogOpen(false);
                  setUploadMethod('device');
                  setFileUrl('');
                  setUrlValidationError('');
                  setIsUrlValidating(false);
                  setIsDownloading(false);
                  setDownloadProgress(0);
                }}
                sx={{ color: '#64748B' }}
              >
                Cancel
              </Button>
            </DialogActions>
          </Dialog>

          {/* New Application Dialog */}
          <Dialog
            open={newApplicationDialogOpen}
            onClose={() => {
              setNewApplicationDialogOpen(false);
              // Reset form data when dialog closes
              setNewApplicationFormData({
                name: '',
                topic: '',
                keywords: []
              });
            }}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Create New Application</Typography>
                <IconButton onClick={() => {
                  setNewApplicationDialogOpen(false);
                  // Reset form data when dialog closes
                  setNewApplicationFormData({
                    name: '',
                    topic: '',
                    keywords: []
                  });
                }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                {/* File Upload Section */}
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#131F32', fontWeight: 600 }}>
                  Choose Upload Method
                </Typography>

                {/* Upload Options Grid */}
                <Grid container spacing={3}>
                  {/* Device Upload Option */}
                  <Grid item xs={12} md={6}>
                    <Card
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        cursor: 'pointer',
                        border: '2px solid #E5E9EF',
                        borderRadius: '12px',
                        backgroundColor: '#F8F9FA',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#9C27B0',
                          backgroundColor: '#F3E5F5',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(156, 39, 176, 0.1)'
                        }
                      }}
                      onClick={() => {
                        // This will trigger the file input
                        document.getElementById('new-application-upload')?.click();
                      }}
                    >
                      <UploadIcon sx={{ fontSize: 48, color: '#9C27B0', mb: 2 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#131F32', mb: 1 }}>
                        Upload from Device
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748B', mb: 2 }}>
                        Select files from your computer or mobile device
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                        Supported: PDF, DOC, DOCX, TXT, Images
                      </Typography>
                    </Card>
                  </Grid>

                  {/* Google Drive Option */}
                  <Grid item xs={12} md={6}>
                    <Card
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        cursor: 'pointer',
                        border: '2px solid #E5E9EF',
                        borderRadius: '12px',
                        backgroundColor: '#F8F9FA',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#9C27B0',
                          backgroundColor: '#F3E5F5',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(156, 39, 176, 0.1)'
                        }
                      }}
                      onClick={() => {
                        setNewApplicationDialogOpen(false);
                        openGoogleDriveDialog();
                      }}
                    >
                      <Box sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        backgroundColor: '#4285F4',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2
                      }}>
                        <Typography sx={{ fontSize: 24, color: 'white', fontWeight: 'bold' }}>
                          G
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#131F32', mb: 1 }}>
                        Google Drive
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748B', mb: 2 }}>
                        Select files from your Google Drive account
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                        Connect and browse your files
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>

                {/* Hidden file input for device upload */}
                <input
                  type="file"
                  id="new-application-upload"
                  hidden
                  onChange={handleNewApplicationUpload}
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.tif,.webp,.heic,.heif"
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button
                onClick={() => {
                  setNewApplicationDialogOpen(false);
                  // Reset form data when dialog closes
                  setNewApplicationFormData({
                    name: '',
                    topic: '',
                    keywords: []
                  });
                }}
                sx={{ color: '#64748B' }}
              >
                Cancel
              </Button>
            </DialogActions>
          </Dialog>


          {/* Google Drive Authentication Dialog */}
          <Dialog
            open={showGoogleDriveAuthDialog}
            onClose={() => setShowGoogleDriveAuthDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{
              pb: 2,
              borderBottom: '1px solid #E5E9EF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#F9FAFB'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: '#4285F4',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Typography sx={{ fontSize: 20, color: 'white', fontWeight: 'bold' }}>
                    G
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#131F32' }}>
                  Google Drive Authentication
                </Typography>
              </Box>
              <IconButton
                onClick={() => setShowGoogleDriveAuthDialog(false)}
                sx={{ color: '#64748B' }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 3, pb: 2 }}>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                {isGoogleDriveAuthInProgress ? (
                  <>
                    <CircularProgress size={48} sx={{ color: '#4285F4', mb: 3 }} />
                    <Typography variant="h6" sx={{ color: '#131F32', mb: 2 }}>
                      Authenticating with Google Drive...
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B' }}>
                      Please complete the authentication in the popup window
                    </Typography>
                  </>
                ) : (
                  <>
                    <Box sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      backgroundColor: '#4285F4',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3
                    }}>
                      <Typography sx={{ fontSize: 32, color: 'white', fontWeight: 'bold' }}>
                        G
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ color: '#131F32', mb: 2 }}>
                      Connect to Google Drive
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B', mb: 3 }}>
                      Click the button below to authenticate with your Google Drive account
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={initiateGoogleDriveAuth}
                      disabled={isGoogleDriveAuthInProgress}
                      sx={{
                        backgroundColor: '#9C27B0',
                        '&:hover': { backgroundColor: '#7B1FA2' },
                        fontWeight: 600
                      }}
                    >
                      {isGoogleDriveAuthInProgress ? 'Authenticating...' : 'Connect to Google Drive'}
                    </Button>
                  </>
                )}
              </Box>
            </DialogContent>
          </Dialog>

          {/* Google Drive File Selection Dialog */}
          <Dialog
            open={showGoogleDriveDialog}
            onClose={closeGoogleDriveDialog}
            maxWidth="md"
            fullWidth
            sx={{ '& .MuiDialog-paper': { height: '80vh' } }}
          >
            <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #E5E9EF' }}>
              Select a file
            </DialogTitle>
            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant={googleDriveActiveTab === 'myDrive' ? 'contained' : 'outlined'}
                    onClick={() => handleGoogleDriveTabChange('myDrive')}
                    size="small"
                    sx={{
                      minWidth: 'auto',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      borderRadius: 1,
                      ...(googleDriveActiveTab === 'myDrive' ? {
                        backgroundColor: '#9C27B0',
                        '&:hover': { backgroundColor: '#7B1FA2' }
                      } : {
                        borderColor: '#d1d5db',
                        color: '#6b7280',
                        '&:hover': {
                          borderColor: '#9ca3af',
                          backgroundColor: '#f9fafb'
                        }
                      })
                    }}
                  >
                    My Drive
                  </Button>
                  <Button
                    variant={googleDriveActiveTab === 'shared' ? 'contained' : 'outlined'}
                    onClick={() => handleGoogleDriveTabChange('shared')}
                    size="small"
                    sx={{
                      minWidth: 'auto',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      borderRadius: 1,
                      ...(googleDriveActiveTab === 'shared' ? {
                        backgroundColor: '#9C27B0',
                        '&:hover': { backgroundColor: '#7B1FA2' }
                      } : {
                        borderColor: '#d1d5db',
                        color: '#6b7280',
                        '&:hover': {
                          borderColor: '#9ca3af',
                          backgroundColor: '#f9fafb'
                        }
                      })
                    }}
                  >
                    Shared with me
                  </Button>
                  <Button
                    variant={googleDriveActiveTab === 'teamDrives' ? 'contained' : 'outlined'}
                    onClick={() => handleGoogleDriveTabChange('teamDrives')}
                    size="small"
                    sx={{
                      minWidth: 'auto',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      borderRadius: 1,
                      ...(googleDriveActiveTab === 'teamDrives' ? {
                        backgroundColor: '#9C27B0',
                        '&:hover': { backgroundColor: '#7B1FA2' }
                      } : {
                        borderColor: '#d1d5db',
                        color: '#6b7280',
                        '&:hover': {
                          borderColor: '#9ca3af',
                          backgroundColor: '#f9fafb'
                        }
                      })
                    }}
                  >
                    Shared drives
                  </Button>
                </Box>
              </Box>

              {/* Search Bar */}
              <Box sx={{ p: 2, borderBottom: '1px solid #E5E9EF' }}>
                <TextField
                  fullWidth
                  placeholder="Search files..."
                  value={googleDriveSearchQuery}
                  onChange={(e) => setGoogleDriveSearchQuery(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#9ca3af', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#9C27B0'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#9C27B0'
                      }
                    }
                  }}
                />
              </Box>

              {/* Folders Section */}
              <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #E5E9EF' }}>
                <Typography variant="subtitle2" sx={{ color: '#666', mb: 1 }}>
                  Folders
                </Typography>
                {isLoadingGoogleDriveFiles ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                    <CircularProgress size={16} sx={{ color: '#9C27B0' }} />
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      Loading folders...
                    </Typography>
                  </Box>
                ) : googleDriveFolders.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {googleDriveFolders.map((folder) => (
                      <Chip
                        key={folder.id}
                        label={folder.name}
                        onClick={() => navigateToFolder(folder.id)}
                        icon={<FolderIcon sx={{ fontSize: 16 }} />}
                        size="small"
                        sx={{
                          backgroundColor: '#f3f4f6',
                          color: '#374151',
                          '&:hover': {
                            backgroundColor: '#e5e7eb'
                          }
                        }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2, color: '#666' }}>
                    <FolderIcon sx={{ fontSize: 20 }} />
                    <Typography variant="body2">No folders available</Typography>
                  </Box>
                )}
              </Box>

              {/* Content Section */}
              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {googleDriveActiveTab === 'teamDrives' ? (
                  // Team Drives View
                  <>
                    <Typography variant="subtitle2" sx={{ color: '#666', mb: 2 }}>
                      Team Drives
                    </Typography>
                    {isLoadingGoogleDriveFiles ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 4 }}>
                        <CircularProgress size={20} sx={{ color: '#9C27B0' }} />
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          Loading team drives...
                        </Typography>
                      </Box>
                    ) : teamDrives.length > 0 ? (
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
                        {teamDrives.map((drive) => (
                          <Card
                            key={drive.id}
                            sx={{
                              p: 2,
                              cursor: 'pointer',
                              border: selectedTeamDrive === drive.id ? '2px solid #9C27B0' : '1px solid #e0e0e0',
                              '&:hover': {
                                boxShadow: '0 4px 12px rgba(156, 39, 176, 0.15)',
                              },
                            }}
                            onClick={() => handleTeamDriveSelect(drive.id)}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <AssessmentIcon sx={{ color: '#9C27B0' }} />
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {drive.name}
                              </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: '#666' }}>
                              {drive.capabilities?.canAddChildren ? 'Can add files' : 'View only'}
                            </Typography>
                          </Card>
                        ))}
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 4, color: '#666' }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          🏢
                        </Typography>
                        <Typography variant="body2">No team drives available</Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  // Files View
                  <>
                    <Typography variant="subtitle2" sx={{ color: '#666', mb: 2 }}>
                      Files
                    </Typography>
                    {isLoadingGoogleDriveFiles ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 4 }}>
                        <CircularProgress size={20} sx={{ color: '#9C27B0' }} />
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          Loading files...
                        </Typography>
                      </Box>
                    ) : filteredGoogleDriveFiles.length > 0 ? (
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 2 }}>
                        {filteredGoogleDriveFiles.map((file) => (
                          <Card
                            key={file.id}
                            sx={{
                              p: 2,
                              cursor: 'pointer',
                              border: selectedGoogleFile?.id === file.id ? '2px solid #9C27B0' : '1px solid #e0e0e0',
                              '&:hover': {
                                boxShadow: '0 4px 12px rgba(156, 39, 176, 0.15)',
                              },
                            }}
                            onClick={() => handleGoogleDriveFileSelect(file, {} as React.MouseEvent)}
                          >
                            <Box sx={{ height: 80, mb: 1, position: 'relative', overflow: 'hidden', borderRadius: 1 }}>
                              {file.mimeType?.includes('image') ? (
                                <ImagePreview file={file} />
                              ) : (
                                <Box
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: file.mimeType?.includes('pdf') ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' :
                                      file.mimeType?.includes('word') || file.mimeType?.includes('document') ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' :
                                        file.mimeType?.includes('sheet') || file.mimeType?.includes('excel') ? 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' :
                                          file.mimeType?.includes('presentation') || file.mimeType?.includes('powerpoint') ? 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' :
                                            'linear-gradient(135deg, #9C27B0 0%, #E1BEE7 100%)',
                                    color: 'white',
                                    borderRadius: '8px',
                                    position: 'relative',
                                    overflow: 'hidden',
                                  }}
                                >
                                  <Typography sx={{ fontSize: '24px', mb: 0.5 }}>
                                    {getFileTypeIcon(file.mimeType)}
                                  </Typography>
                                  <Typography sx={{ fontSize: '10px', textAlign: 'center', px: 1 }}>
                                    {getFileTypeLabel(file.mimeType)}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                textAlign: 'center',
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {file.name}
                            </Typography>
                          </Card>
                        ))}
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 4, color: '#666' }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          📄
                        </Typography>
                        <Typography variant="body2">
                          {googleDriveSearchQuery ? 'No files match your search' : 'No files available'}
                        </Typography>
                        {googleDriveSearchQuery && (
                          <Button
                            size="small"
                            onClick={() => setGoogleDriveSearchQuery('')}
                            sx={{ mt: 1, color: '#9C27B0' }}
                          >
                            Clear search
                          </Button>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, borderTop: '1px solid #E5E9EF' }}>
              <Button onClick={closeGoogleDriveDialog}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  if (selectedGoogleFile) {
                    downloadGoogleDriveFile(selectedGoogleFile);
                  } else if (selectedGoogleFiles.length > 0) {
                    downloadMultipleGoogleDriveFiles(selectedGoogleFiles);
                  } else {
                    setError('Please select at least one file to download');
                  }
                }}
                disabled={!selectedGoogleFile && selectedGoogleFiles.length === 0}
                sx={{
                  backgroundColor: '#9C27B0',
                  '&:hover': { backgroundColor: '#7B1FA2' },
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(156, 39, 176, 0.3)',
                    color: 'rgba(255, 255, 255, 0.5)'
                  }
                }}
              >
                {isDownloadingGoogleFile ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} sx={{ color: 'white' }} />
                    Downloading...
                  </Box>
                ) : (
                  `Download ${selectedGoogleFile ? 'File' : `${selectedGoogleFiles.length} Files`}`
                )}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Export Application Data Dialog */}
          <Dialog
            open={exportDialogOpen}
            onClose={() => setExportDialogOpen(false)}
            maxWidth="xl"
            fullWidth
          >
            <DialogTitle sx={{
              pb: 2,
              borderBottom: '1px solid #E5E9EF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#F9FAFB'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <DownloadIcon sx={{ color: '#9C27B0', fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#131F32' }}>
                  Application Data Export
                </Typography>
              </Box>
              <IconButton
                onClick={() => setExportDialogOpen(false)}
                sx={{ color: '#64748B' }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 3, pb: 2 }}>
              {exportData ? (
                <Box sx={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  maxHeight: '70vh',
                  overflow: 'hidden',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}>
                  <Box sx={{
                    p: 3,
                    borderBottom: '1px solid #F3F4F6',
                    backgroundColor: '#F9FAFB'
                  }}>
                    {/* <Typography variant="subtitle2" sx={{ 
                  fontWeight: 600, 
                  color: '#374151',
                  mb: 1
                }}>
                  Raw JSON Data
                </Typography> */}
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                      Click on object keys to expand/collapse sections
                    </Typography>
                  </Box>
                  <Box sx={{
                    p: 3,
                    maxHeight: 'calc(70vh - 120px)',
                    overflow: 'auto'
                  }}>
                    {exportData && Object.keys(exportData).length > 0 ? (
                      <Box paddingBottom={10}>
                        {/* Debug info */}

                        <JsonViewer data={exportData} />

                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" sx={{ color: '#6B7280' }}>
                          No data available to display
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <CircularProgress size={40} sx={{ color: '#9C27B0', mb: 2 }} />
                  <Typography variant="body1" sx={{ color: '#374151', mb: 1 }}>
                    Preparing export data...
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6B7280' }}>
                    This may take a few moments
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E9EF', backgroundColor: '#F9FAFB' }}>
              <Button
                onClick={() => setExportDialogOpen(false)}
                sx={{
                  color: '#6B7280',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: '#F3F4F6'
                  }
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* Project Creation Dialog - Moved outside conditional rendering */}
      <Dialog
        open={isProjectDialogOpen}
        onClose={() => setIsProjectDialogOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{
          zIndex: 9999,
          '& .MuiDialog-paper': {
            zIndex: 9999
          }
        }}
      >
        <DialogTitle sx={{
          pb: 1,
          borderBottom: '1px solid #E5E9EF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#131F32' }}>
            Create New Project
          </Typography>
          <IconButton
            onClick={() => setIsProjectDialogOpen(false)}
            sx={{ color: '#64748B' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3} pt={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Project Name *"
                value={newProject.project_name}
                onChange={(e) => setNewProject(prev => ({ ...prev, project_name: e.target.value }))}
                size="small"
                error={!!projectError && !newProject.project_name.trim()}
                helperText={projectError && !newProject.project_name.trim() ? 'Project name is required' : ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Project Type"
                value={newProject.project_type}
                onChange={(e) => setNewProject(prev => ({ ...prev, project_type: e.target.value }))}
                size="small"
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Project Description"
                value={newProject.project_description}
                onChange={(e) => setNewProject(prev => ({ ...prev, project_description: e.target.value }))}
                multiline
                rows={3}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={newProject.period_start_date}
                onChange={(e) => setNewProject(prev => ({ ...prev, period_start_date: e.target.value }))}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={newProject.period_end_date}
                onChange={(e) => setNewProject(prev => ({ ...prev, period_end_date: e.target.value }))}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => {
              setNewProject({
                project_name: '',
                project_description: '',
                project_type: 'EKYC_DOCU',
                period_start_date: new Date().toISOString().split('T')[0],
                period_end_date: new Date().toISOString().split('T')[0],
                period_type: 'daily',
                source: 'manual',
                active: true,
                status: 'active'
              });
              setProjectError('');
              setIsProjectDialogOpen(false);
            }}
            sx={{ color: '#64748B' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateProject}
            variant="contained"
            disabled={isSubmittingProject}
            sx={{
              backgroundColor: '#9C27B0',
              '&:hover': { backgroundColor: '#7B1FA2' },
              fontWeight: 600
            }}
          >
            {isSubmittingProject ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Creating...
              </>
            ) : (
              'Create Project'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoanProcessing; 